import * as React from "react";
import * as ReactDOM from "react-dom";
import { Route } from "react-router-dom";
import * as moment from "moment";
import * as marked from "marked";
import * as enums from "../../enumerations";
import * as modelsGet from "../../models/get";
import * as modelsPost from "../../models/post";
import * as modelsCalendar from "../../models/calendar";
import * as modelsForm from "../../models/form";
import * as utils from "../../utils/utils";
import * as utilsTextarea from "../../utils/textarea";
import * as utilsCalendar from "../../utils/calendar";
import * as globals from "../../globals";
import * as apiEvents from "../../api/events";
import * as apiPersons from "../../api/persons";
import * as apiActivities from "../../api/activities";
import * as apiTimes from "../../api/times";
import * as apiSubjects from "../../api/subjects";
import * as apiGraveTypes from "../../api/graveTypes";
import * as apiComments from "../../api/comments";
import * as apiChangesets from "../../api/changesets";
import { Window } from "../window";
import { Loader, LoaderStyle } from "../loader";
import { PopupProvider, Popup, PopupTrigger, PopupAlign } from "../popup";
import { Label, LabelColor } from "../label";
import { Button, ButtonType, ButtonSize } from "../button";
import { ScrollViewer, ScrollBarVisibility } from "../scrollViewer";
import { ContextMenu, MenuItem, Separator } from "../contextmenu/index";
import { TabControl, Tab } from "../tabControl";
import { Expander } from "../expander";
import { FormGroup } from "../form";
import { ComboBox, ComboBoxType, DROPDOWN_TEXT_ITEM_HEIGHT, DropDownTextItem } from "../comboBox";
import { InputMask, InputSize } from "../input";
import { InputValueType } from "../../enumerations";
import { TextBox } from "../textBox";
import { Alert, AlertType } from "../alert";
import { ToolBar, ToolBarItem, ToolBarSeparator } from "../toolbar";
import { TextArea } from "../textArea";
import { ListView, ListViewActivityItem } from "../listView";
import { Time } from "../../types/time";
import { Table, ITableColumn, ITableRowEventArgs } from "../table";
import { DeleteDialog } from "./deleteDialog";

enum Mode
{
    create,
    edit,
    copy,
    followUp
}

interface IInitOptions
{
    props: IProperties,
    appointmentId?: number,
    currentUser: modelsGet.IUser,
    calendarGroups: modelsGet.ICalendarGroup[],
    calendars: modelsGet.ICalendar[],
    status: modelsGet.IStatus[],
    displayTypes: modelsGet.IDisplayType[],
    burialMethods: modelsGet.IBurialMethod[],
    morticians: modelsGet.IPerson[],
    pastors: modelsGet.IPerson[],
    denominations: modelsGet.IDenomination[],
    labels: modelsGet.ILabel[],
    templates: modelsGet.ITemplate[],
    mourningHalls: modelsGet.IMourningHall[],
    settings: {
        eventFields?: string[]
    }
}

interface IProperties
{
    /**
     * Die vorherige oder momentane Route. Wenn der Dialog als Modal geöffnet wird,
     * ist hier die vorherige Route drin.
     */
    route: () => uri.URI,

    /**
     * Die momentane Route. Wenn der Dialog als Modal geöffnet wird,
     * ist hier die eigentliche Route für den Termin Dialog drin.
     */
    currentRoute: () => uri.URI,

    /**
     * Das react-router history object.
     */
    history: any,

    /**
     * Die ID des Termins der geöffnet werden soll. (Ist leer wenn ein neuer Termin erstellt werden soll.)
     */
    appointmentId?: number,

    /**
     * Der selektierte Kalender oder die Kalendergruppe.
     */
    selectedObject: modelsCalendar.ISelectedObjectId,

    /**
     * Selektierte Datum.
     */
    selectedDate: moment.Moment,

    /**
     * Der selektierte Kalender (nur wenn eine Kalendergruppe ausgewählt ist).
     */
    selectedCalendar: number,

    /**
     * Gibt an, ob der Dialog aus React geöffnet wurde oder via Link.
     */
    isModal: boolean,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt an, ob alle Schaltflächen und Controls abgeschaltet sind.
     */
    isEverythingDisabled?: boolean,

    /**
     * Gibt an, ob der Erstell/Speichern Button abgeschaltet ist.
     */
    isSaveButtonDisabled?: boolean,

    /**
     * Gibt an, ob die Betreff ComboBox abgeschaltet ist.
     */
    isSubjectDisabled?: boolean,

    /**
     * Gibt an, ob die Status ComboBox abgeschaltet ist.
     */
    isStatusDisabled?: boolean,

    /**
     * Gibt an, ob die Grabart ComboBox abgeschaltet ist.
     */
    isGraveTypeDisabled?: boolean,

    /**
     * Gibt an, ob die "Zeit bis" ComboBox abgeschaltet ist.
     */
    isTimeToDisabled?: boolean,

    /**
     * Gibt an, ob die Zeit nur via einer ComboBox ausgewählt werden darf.
     */
    isTimeUserDefinable?: boolean,

    /**
     * Gibt an, ob der Termin gelöscht werden darf/kann.
     */
    isEventDeletable?: boolean,

    /**
     * Gibt an, ob der Termin gedruckt werden darf/kann.
     */
    isEventPrintible?: boolean,

    /**
     * Gibt an, ob der Dialog über einen freien Block geöffnet wurde und der Benutzer keine beliebigen Zeiten vergeben darf.
     */
    isInBlockMode?: boolean,

    /**
     * Gibt an, ob der Benutzer nur bestimmte Felder bearbeiten darf.
     */
    isUserLimited?: boolean,

    /**
     * Gibt an, ob der Benutzer ein Bestatter ist.
     */
    isUserAMortician?: boolean,

    /**
     * Gibt an, ob der Expander "Allgemein" offen ist.
     */
    isGeneralExpanded?: boolean,

    /**
     * Gibt an, ob der Expander "Verstorbener" offen ist.
     */
    isDeceasedExpanded?: boolean,

    /**
     * Gibt an, ob der Expander "Auftraggeber" offen ist.
     */
    isCustomerExpanded?: boolean,

    /**
     * Gibt an, ob der Dialog zum löschen eines Termins offen ist.
     */
    isDeleteDialogOpen?: boolean,

    /**
     * Gibt den Modus an in dem sich der Dialog befindet.
     */
    mode?: Mode,

    /**
     * Enthält alle von der Komponente benötigten Einstellungen.
     */
    settings?: {
        eventFields?: string[]
    },

    /**
     * Enthält den momentan selektierten Kalender.
     */
    selectedObject?: modelsCalendar.ISelectedObject,

    /**
     * Der selektierte Kalender (nur wenn eine Kalendergruppe ausgewählt ist).
     */
    selectedCalendar?: modelsGet.ICalendar,

    /**
     * Der angemeldete Benutzer.
     */
    currentUser?: modelsGet.IUser,

    /**
     * Wenn im Bearbeitungsmodus, dann ist diese Eigenschaft gefüllt.
     */
    appointment?: modelsGet.IAppointment,

    /**
     * Enthält alle Labels des geladenen Termins.
     */
    selectedLabels?: modelsGet.ILabel[],

    /**
     * Enthält alle Labels für das Popup zum auswählen von Labels.
     */
    popupLabels?: modelsGet.ILabel[],

    /**
     * Enthält alle Betreffzeilen zum geladenen Friedhof.
     */
    subjects?: modelsGet.ISubject[],

    /**
     * Alle eingerichteten Kalendergruppen.
     */
    calendarGroups?: modelsGet.ICalendarGroup[],

    /**
     * Alle eingerichteten Kalender.
     */
    calendars?: modelsGet.ICalendar[],

    /**
     * Alle eingerichteten Status Einträge.
     */
    status?: modelsGet.IStatus[],

    /**
     * Alle eingerichteten Anzeigearten.
     */
    displayTypes?: modelsGet.IDisplayType[],

    /**
     * Alle eingerichteten Bestattungsarten.
     */
    burialMethods?: modelsGet.IBurialMethod[],

    /**
     * Enthält alle Grabarten zum geladenen Friedhof.
     */
    graveTypes?: modelsGet.IGraveType[],

    /**
     * Alle eingerichteten Bestatter.
     */
    morticians?: modelsGet.IPerson[],

    /**
     * Alle eingerichteten Pfarrer.
     */
    pastors?: modelsGet.IPerson[],

    /**
     * Alle eingerichteten Konfessionen.
     */
    denominations?: modelsGet.IDenomination[],

    /**
     * Alle eingerichteten labels.
     */
    labels?: modelsGet.ILabel[],

    /**
     * Alle eingerichteten Templates.
     */
    templates?: modelsGet.ITemplate[],

    /**
     * Alle eingerichteten Trauerhallen.
     */
    mourningHalls?: modelsGet.IMourningHall[],

    /**
     * Enthält die Auswahlmöglichkeiten der "Zeit von" ComboBox.
     */
    timeFrom?: modelsGet.ITime[],

    /**
     * Enthält die Auswahlmöglichkeiten der "Zeit bis" ComboBox.
     */
    timeTo?: modelsGet.ITime[],

    /**
     * Enthält alle Aktivitäten des geladenen Termins.
     */
    activities?: modelsGet.IActivity[],

    /**
     * Enthält das erste Changeset des Termins.
     */
    firstChangeset?: modelsGet.IChangeset<modelsGet.IAppointment>,

    /**
     * Enthält Daten für den Status Graph.
     */
    transitions?: modelsCalendar.ITransition[],

    /**
     * Enthält einen Info Text zur letzten Änderung des Termins.
     */
    lastActivity?: string,

    /**
     * Enthält den Index des momentan selektierten ListViewItem in den Aktivitäten.
     */
    activitySelectedIndex?: number,

    /**
     * Enthält die Daten des momentan selektierten ListViewItem in den Aktivitäten.
     */
    activitySelectedData?: modelsGet.IActivity,

    /**
     * Enthält alle Verlinkungen des Termins.
     */
    links?: modelsGet.ILink[],

    /**
     * Enthält den Index des momentan selektierten Tabs.
     */
    selectedTabIndex?: number,

    /**
     * Enthält Form Request Errors.
     */
    errors?: Object,

    /**
     * Enthält eine Alert Meldung. Die Meldung wird nur dann angezeigt wenn
     * diese Eigenschaft gefüllt ist.
     */
    alertMessage?: string,

    /**
     * Enthält den Typ des Alerts.
     */
    alertType?: AlertType,

    /**
     * Enthält alle Eigenschaften des Forms.
     */
    form?: {
        subject?: modelsForm.IComboBoxValue<string, number>,
        calendar?: modelsForm.IComboBoxValue<string, number>,
        status?: modelsForm.IComboBoxValue<string, number>,
        displayType?: modelsForm.IComboBoxValue<string, number>,
        startdate?: string,
        timeFrom?: modelsForm.IComboBoxValue<string, string>,
        timeTo?: modelsForm.IComboBoxValue<string, string>,
        setuptimeBefore?: number,
        setuptimeAfter?: number,
        graveNo?: string,
        graveType?: modelsForm.IComboBoxValue<string, string>,
        burialMethod?: modelsForm.IComboBoxValue<string, number>,
        mortician?: modelsForm.IComboBoxValue<string, number>,
        pastor?: modelsForm.IComboBoxValue<string, number>,
        mourningHall?: modelsForm.IComboBoxValue<string, number>,
        funeralGuestsCount?: number,
        remark?: string,
        remark2?: string,

        title?: string,
        lastname?: string,
        firstname?: string,
        birthname?: string,
        street?: string,
        streetNo?: string,
        country?: string,
        zipCode?: string,
        city?: string
        dateOfBirth?: string,
        dateOfDeath?: string,
        placeOfDeath?: string,
        denomination?: modelsForm.IComboBoxValue<string, number>,

        customerTitle?: string,
        customerLastname?: string,
        customerFirstname?: string,
        customerStreet?: string,
        customerStreetNo?: string,
        customerCountry?: string,
        customerZipCode?: string,
        customerCity?: string,
        customerPhoneNumber?: string,

        comment?: string,
    }
}

export class EventDialog extends React.Component<IProperties, IState>
{
    private _isFirstInit: boolean;
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean;
    private _createOnlyComment: boolean;
    private _commentTextAreaRef: HTMLTextAreaElement;

    /**
     * Erstellt eine neue Instanz der Klasse @see EventDialog.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        var mode: Mode;
        var uri: uri.URI = props.currentRoute();
        var copyId: number = uri.getQueryParameterValue<number>("copy");
        var followUpId: number = uri.getQueryParameterValue<number>("followUp");

        if (props.appointmentId > 0)
        {
            mode = Mode.edit;
        }
        else if (copyId > 0)
        {
            mode = Mode.copy;
        }
        else if (followUpId > 0)
        {
            mode = Mode.followUp;
        }
        else
        {
            mode = Mode.create;
        }

        this.state = {
            isLoading: true,
            mode: mode,
            settings: {},
            isEverythingDisabled: false,
            isSaveButtonDisabled: true,
            isSubjectDisabled: false,
            isGraveTypeDisabled: false,
            isTimeToDisabled: false,
            isGeneralExpanded: true,
            isDeceasedExpanded: true,
            isCustomerExpanded: true,
            isDeleteDialogOpen: false,
            selectedLabels: [],
            form: {
                subject: {},
                calendar: {},
                status: {},
                displayType: {},
                timeFrom: {},
                timeTo: {},
                graveType: {},
                burialMethod: {},
                mortician: {},
                pastor: {},
                mourningHall: {},
                denomination: {}
            },
            errors: {}
        }

        this._isSaveButtonDisabled = this.state.isSaveButtonDisabled;

        if (this.state.mode === Mode.edit)
            this.initAsync(props, props.appointmentId);
        else if (this.state.mode === Mode.copy)
            this.initAsync(props, copyId);
        else if (this.state.mode === Mode.followUp)
            this.initAsync(props, followUpId);
        else
            this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties, appointmentId?: number)
    {
        // Benutzer laden.
        var currentUser: modelsGet.IUser = await globals.getCurrentUserAsync();

        // Einstellungen laden.
        var eventFields = await globals.getSettingAsync<string[]>(globals.SettingKey.eventFields);

        // Alle eingerichteten Kalendergruppen laden.
        var calendarGroups: modelsGet.ICalendarGroup[] = await globals.getCalendarGroupsAsync();

        // Alle eingerichteten Kalender laden.
        var calendars: modelsGet.ICalendar[] = await globals.getCalendarsAsync();

        // Alle eingerichteten Status Einträge laden.
        var status: modelsGet.IStatus[] = await globals.getStatusAsync();

        // Alle eingerichteten Anzeigearten laden.
        var displayTypes: modelsGet.IDisplayType[] = await globals.getDisplayTypesAsync();

        // Alle eingerichteten Bestattungsarten laden.
        var burialMethods: modelsGet.IBurialMethod[] = await globals.getBurialMethodsAsync();

        // Alle eingerichteten Bestatter laden.
        var morticians: modelsGet.IPerson[] = await globals.getMorticiansAsync();

        // Alle eingerichteten Pfarrer laden.
        var pastors: modelsGet.IPerson[] = await globals.getPastrosAsync();

        // Alle eingerichteten Konfessionen laden.
        var denominations: modelsGet.IDenomination[] = await globals.getDenominationsAsync();

        // Alle eingerichteten labels laden.
        var labels: modelsGet.ILabel[] = await globals.getLabelsAsync();

        // Alle eingerichteten Templates laden.
        var templates: modelsGet.ITemplate[] = await globals.getTemplatesAsync();

        // Alle eingerichteten Trauerhallen laden.
        var mourningHalls: modelsGet.IMourningHall[] = await globals.getMourningHallsAsync();

        // Parameter für init functions erstellen.
        var options: IInitOptions = {
            props: props,
            appointmentId: appointmentId,
            currentUser: currentUser,
            settings: {
                eventFields: eventFields
            },
            calendarGroups: calendarGroups,
            calendars: calendars,
            status: status,
            displayTypes: displayTypes,
            burialMethods: burialMethods,
            morticians: morticians,
            pastors: pastors,
            denominations: denominations,
            labels: labels,
            templates: (templates || []).where(t => !currentUser.claims.forbidden_reports.contains(t.id)),
            mourningHalls: mourningHalls
        };

        if (this.state.mode === Mode.edit)
        {
            await this.initEditModeAsync(options);
        }
        else if (this.state.mode === Mode.create)
        {
            await this.initCreateModeAsync(options);
        }
        else if (this.state.mode === Mode.copy)
        {
            await this.initCopyModeAsync(options);
        }
        else if (this.state.mode === Mode.followUp)
        {
            await this.initFollowUpModeAsync(options);
        }
    }

    /**
     * Initialisert den Create Mode.
     * @param options
     */
    private async initCreateModeAsync(options: IInitOptions)
    {
        const { props, appointmentId, currentUser, settings } = options;

        // Momentan selektierten Kalender oder Kalendergruppe ermitteln.
        var selectedObject: modelsCalendar.ISelectedObject;
        var uri: uri.URI = this.props.currentRoute();
        var calendarId: number = uri.getQueryParameterValue<number>("calendar");
        var selectedCalendar: modelsGet.ICalendar = null;

        if (calendarId > 0)
        {
            selectedCalendar = options.calendars.where(c => c.id === calendarId).first();
        }
        else
        {
            if (props.selectedCalendar != null && props.selectedCalendar > 0)
                selectedCalendar = options.calendars.where(c => c.id === props.selectedCalendar).first();
        }

        var promise = utilsCalendar.getSelectedObject(props.selectedObject, options.calendarGroups, options.calendars);
        promise.calendar((calendar) =>
        {
            selectedObject = {
                type: enums.CalendarObjectType.calendar,
                data: calendar
            };

            if (!(calendarId > 0))
                selectedCalendar = calendar;
        });
        promise.calendarGroup((group) =>
        {
            selectedObject = {
                type: enums.CalendarObjectType.calendarGroup,
                data: group
            };
        });

        var popupLabels: modelsGet.ILabel[] = [];

        // Labels für das Popup ermitteln.
        if (options.labels != null && options.labels.length > 0)
        {
            for (let label of options.labels)
            {
                popupLabels.push({
                    ...label
                });
            }
        }

        // Zur verfügung stehende Zeiten ermitteln.
        var prevUri: uri.URI = this.props.route();
        var uri: uri.URI = this.props.currentRoute();
        var viewMode: enums.CalendarViewMode = prevUri.getViewMode();
        var startdate: moment.Moment = uri.getQueryParameterValue<moment.Moment>("date");
        var enddate: moment.Moment = uri.getQueryParameterValue<moment.Moment>("enddate");
        var setuptime: number = uri.getQueryParameterValue<number>("setuptime");
        var timeFromInputValue: any;
        var timeToInputValue: any;
        var timeFrom: modelsGet.ITime[] = await apiTimes.getAvailableTime(selectedCalendar.id);
        var timeTo: modelsGet.ITime[] = [];
        if (viewMode !== enums.CalendarViewMode.month)
        {
            timeFromInputValue = {
                value: startdate.format("HH:mm"),
                selectedValue: startdate.format("HH:mm")
            };

            if (enddate != null)
            {
                timeToInputValue = {
                    value: enddate.format("HH:mm"),
                    selectedValue: enddate.format("HH:mm")
                };
            }

            timeTo = await apiTimes.getAvailableTime(selectedCalendar.id, startdate.format("HH:mm"));
        }

        // Alle Betreffzeilen ermitteln.
        var subjects: modelsGet.ISubject[] = await apiSubjects.getSubjectsByCalendarId(selectedCalendar.id);

        // Default Status ermitteln.
        var defaultStatus = options.status.where(s => s.is_default).first();

        // Default Anzeigeart ermitteln.
        var defaultDisplayType = options.displayTypes.where(d => d.is_default).first();

        // Alle Grabarten ermitteln.
        var graveTypes: modelsGet.IGraveType[] = await apiGraveTypes.getGraveTypesByCalendarId(selectedCalendar.id);

        // Alle Templates ohne Listen laden.
        var templates: modelsGet.ITemplate[] = options.templates.where(t => !t.is_list);

        // Berechtigungen prüfen.
        var priviliges: IState = this.initPriviliges(currentUser, selectedCalendar);

        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.isEverythingDisabled = priviliges.isEverythingDisabled;
            newState.isStatusDisabled = priviliges.isStatusDisabled;
            newState.isTimeUserDefinable = priviliges.isTimeUserDefinable;
            newState.isEventDeletable = priviliges.isEventDeletable;
            newState.isEventPrintible = priviliges.isEventPrintible;
            newState.isInBlockMode = priviliges.isInBlockMode;
            newState.isUserLimited = priviliges.isUserLimited;
            newState.isUserAMortician = priviliges.isUserAMortician;

            newState.settings = settings;
            newState.selectedObject = selectedObject;
            newState.selectedCalendar = selectedCalendar;
            newState.currentUser = currentUser;

            newState.popupLabels = popupLabels;
            newState.subjects = subjects;
            newState.calendarGroups = options.calendarGroups;
            newState.calendars = options.calendars;
            newState.status = options.status;
            newState.displayTypes = options.displayTypes;
            newState.burialMethods = options.burialMethods;
            newState.graveTypes = graveTypes;
            newState.morticians = options.morticians;
            newState.pastors = options.pastors;
            newState.denominations = options.denominations;
            newState.labels = options.labels;
            newState.templates = templates;
            newState.mourningHalls = options.mourningHalls;
            newState.timeFrom = timeFrom;
            newState.timeTo = timeTo;

            newState.alertMessage = null;
            newState.alertType = null;

            // Form Daten leeren.
            newState.form = {
                subject: {},
                calendar: {},
                status: {},
                displayType: {},
                timeFrom: {},
                timeTo: {},
                graveType: {},
                burialMethod: {},
                mortician: {},
                pastor: {},
                mourningHall: {},
                denomination: {}
            };

            // From Daten setzen.
            newState.form.calendar = {
                value: selectedCalendar.name,
                selectedValue: selectedCalendar.id
            };

            if (defaultStatus != null)
            {
                newState.form.status = {
                    value: defaultStatus.status,
                    selectedValue: defaultStatus.id
                };
            }

            if (defaultDisplayType != null)
            {
                newState.form.displayType = {
                    value: defaultDisplayType.text,
                    selectedValue: defaultDisplayType.id
                };
            }

            newState.form.startdate = startdate.format("L");

            if (timeFromInputValue != null)
            {
                newState.form.timeFrom = timeFromInputValue;
                newState.isTimeToDisabled = false;
            }
            else
            {
                newState.isTimeToDisabled = true;
            }

            if (timeToInputValue != null)
            {
                newState.form.timeTo = timeToInputValue;
            }

            if (setuptime != null && setuptime > 0)
            {
                newState.form.setuptimeAfter = setuptime;
            }

            if (priviliges.isUserAMortician)
            {
                let mortician: modelsGet.IPerson = options.morticians.where(p => p.id === currentUser.mortician_id).first()
                newState.form.mortician = {
                    value: mortician.fullname_and_address_without_title_rev,
                    selectedValue: mortician.id
                }
            }

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Initialisert den Edit Mode.
     * @param options
     */
    private async initEditModeAsync(options: IInitOptions)
    {
        const { props, appointmentId, currentUser, settings } = options;

        // Termin aus der Datenbank laden.
        var appointment = await apiEvents.getEvent(appointmentId);

        // Momentan selektierten Kalender (Wenn eine Gruppe ausgewählt ist).
        var selectedCalendar: modelsGet.ICalendar = null;
        if (props.selectedCalendar != null && props.selectedCalendar > 0)
        {
            selectedCalendar = options.calendars.where(c => c.id === props.selectedCalendar).first();
        }
        else
        {
            let prevUri: uri.URI = this.props.route();
            let uri: uri.URI = this.props.currentRoute();
            let viewMode: enums.CalendarViewMode = prevUri.getViewMode();
            let calendarId = uri.getQueryParameterValue<number>("calendar");

            if (viewMode === enums.CalendarViewMode.planning || prevUri.path().indexOf("profile") > -1)
                selectedCalendar = options.calendars.where(c => c.id === calendarId).first();
        }

        // Momentan selektierten Kalender oder Kalendergruppe ermitteln.
        var selectedObject: modelsCalendar.ISelectedObject;
        var promise = utilsCalendar.getSelectedObject(props.selectedObject, options.calendarGroups, options.calendars);
        promise.calendar((calendar) =>
        {
            selectedObject = {
                type: enums.CalendarObjectType.calendar,
                data: calendar
            };

            selectedCalendar = calendar;
        });
        promise.calendarGroup((group) =>
        {
            selectedObject = {
                type: enums.CalendarObjectType.calendarGroup,
                data: group
            };
        });

        // Selektierte Labels ermitteln.
        var selectedLabels: modelsGet.ILabel[] = [];
        var popupLabels: modelsGet.ILabel[] = [];

        if (appointment != null && appointment.labels != null)
        {
            for (let eventLabel of appointment.labels)
            {
                selectedLabels.push({
                    label_id: eventLabel.label_id,
                    label: eventLabel.label,
                    label_color: eventLabel.label_color,
                    label_is_visible: eventLabel.label_is_visible,
                    label_initial: eventLabel.label_initial
                });
            }
        }

        if (selectedLabels != null && selectedLabels.length > 0)
        {
            for (let label of options.labels)
            {
                let lbl: modelsGet.ILabel = selectedLabels.where(l => l.label_id === label.label_id).first();
                if (lbl == null)
                {
                    popupLabels.push({
                        ...label
                    });
                }
            }
        }
        else
        {
            for (let label of options.labels)
            {
                popupLabels.push({
                    ...label
                });
            }
        }

        // Letztes Changeset ermitteln.
        var firstChangeset = await apiChangesets.getFirstChangeset<modelsGet.IAppointment>(appointment.id);

        // Alle Aktivitäten ermitteln.
        var activities = await apiActivities.getActivities(appointment.id);

        // Letzte Änderung ermitteln.
        var activity: modelsGet.ILastActivity = await apiActivities.getLastActivity(appointment.id);
        var lastActivity: string;
        if (activity != null)
        {
            if (firstChangeset != null)
            {
                var changesetFullname: string = firstChangeset.user_fullname;
                var changesetDate: moment.Moment = moment(firstChangeset.change_date);
                var activityFullname: string = activity.fullname;
                var activityDate: moment.Moment = moment(activity.change_date);

                if (changesetFullname.toLowerCase() === activityFullname.toLowerCase() && changesetDate.isSame(activityDate))
                {
                    lastActivity = `Erstellt von ${changesetFullname} am ${changesetDate.format("L")}`;
                }
                else
                {
                    lastActivity = `Erstellt von ${changesetFullname} am ${changesetDate.format("L")} und `;
                    lastActivity += `zuletzt geändert von ${activityFullname} am ${activityDate.format("L")}`;
                }
            }
            else
            {
                lastActivity = `Zuletzt geändert von ${activity.fullname} am ${moment(activity.change_date).format("L")}`;
            }
        }
        else if (firstChangeset != null)
        {
            lastActivity = `Erstellt von ${firstChangeset.user_fullname} ${moment(firstChangeset.change_date).format("L")}`;
        }

        // Status Graph (Transitions).
        var transitions: modelsCalendar.ITransition[] = [];

        if (firstChangeset != null)
        {
            var firstEvent: modelsGet.IAppointment = firstChangeset.changeset;
            transitions.push({
                status_color: firstEvent.status_color,
                status_text: (firstEvent.status_id > 0) ? firstEvent.status : "Kein Status",
                change_date: moment(firstChangeset.change_date),
                fullname: firstEvent.user_fullname || firstChangeset.user_fullname,
                profile_picture: firstEvent.user_avatar
            });
        }

        if (activities != null)
        {
            let filteredActivities = activities.where(a => a.type === enums.ActivityType.Status);
            for (var i = filteredActivities.length - 1; i >= 0; i--)
            {
                transitions.push({
                    status_color: filteredActivities[i].new_status.color,
                    status_text: (filteredActivities[i].new_status.id > 0) ? filteredActivities[i].new_status.status : "Kein Status",
                    change_date: moment(filteredActivities[i].editor_date),
                    fullname: filteredActivities[i].editor_fullname,
                    profile_picture: filteredActivities[i].editor_profile_picture
                });
            }
        }

        // Alle Verlinkungen ermitteln.
        var links = await apiEvents.getEventLinks(appointment.id, appointment.parent_event_id);

        // Zur verfügung stehende Zeiten ermitteln.
        var startdate: moment.Moment = moment(appointment.starttime);
        var enddate: moment.Moment = moment(appointment.endtime)
        var timeFrom = await apiTimes.getAvailableTime(appointment.calendar_id);
        var timeTo = await apiTimes.getAvailableTime(appointment.calendar_id, startdate.format("HH:mm"));

        // Alle Betreffzeilen ermitteln.
        var subjects: modelsGet.ISubject[] = await apiSubjects.getSubjectsByCalendarId(appointment.calendar_id);

        // Alle Grabarten ermitteln.
        var graveTypes: modelsGet.IGraveType[] = await apiGraveTypes.getGraveTypesByCalendarId(appointment.calendar_id);

        // Den im Termin zugeordneten Bestatter ermitteln.
        var mortician: modelsGet.IPerson;
        if (appointment.persons != null && appointment.persons.length > 0)
        {
            mortician = appointment.persons.where(p => p.is_mortician).first();
        }

        // Den im Termin zugeordneten Pfarrer ermitteln.
        var pastor: modelsGet.IPerson;
        if (appointment.persons != null && appointment.persons.length > 0)
        {
            pastor = appointment.persons.where(p => p.is_pastor).first();
        }

        // Den im Termin zugeordneten verstorbenen ermitteln.
        var deceased: modelsGet.IPerson;
        if (appointment.persons != null && appointment.persons.length > 0)
        {
            deceased = appointment.persons.where(p => p.is_deceased).first();
        }

        // Den im Termin zugeordneten verstorbenen ermitteln.
        var customer: modelsGet.IPerson;
        if (appointment.persons != null && appointment.persons.length > 0)
        {
            customer = appointment.persons.where(p => p.is_customer).first();
        }

        // Alle Templates ohne Listen laden.
        var templates: modelsGet.ITemplate[] = options.templates.where(t => !t.is_list);

        // Berechtigungen prüfen.
        var priviliges: IState = this.initPriviliges(currentUser, selectedCalendar, appointment);

        // Neues State Objekt erstellen.
        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.isEverythingDisabled = priviliges.isEverythingDisabled;
            newState.isStatusDisabled = priviliges.isStatusDisabled;
            newState.isTimeUserDefinable = priviliges.isTimeUserDefinable;
            newState.isEventDeletable = priviliges.isEventDeletable;
            newState.isEventPrintible = priviliges.isEventPrintible;
            newState.isInBlockMode = priviliges.isInBlockMode;
            newState.isUserLimited = priviliges.isUserLimited;
            newState.isUserAMortician = priviliges.isUserAMortician;

            newState.settings = settings;
            newState.selectedObject = selectedObject;
            newState.selectedCalendar = selectedCalendar;
            newState.currentUser = currentUser;

            newState.appointment = appointment;
            newState.selectedLabels = selectedLabels;
            newState.popupLabels = popupLabels;
            newState.subjects = subjects;
            newState.calendarGroups = options.calendarGroups;
            newState.calendars = options.calendars;
            newState.status = options.status;
            newState.displayTypes = options.displayTypes;
            newState.burialMethods = options.burialMethods;
            newState.graveTypes = graveTypes;
            newState.morticians = options.morticians;
            newState.pastors = options.pastors;
            newState.denominations = options.denominations;
            newState.labels = options.labels;
            newState.templates = templates;
            newState.mourningHalls = options.mourningHalls;
            newState.timeFrom = timeFrom;
            newState.timeTo = timeTo;

            newState.firstChangeset = firstChangeset;
            newState.transitions = transitions;
            newState.activities = activities.where(a => a.type !== 0);
            newState.lastActivity = lastActivity;

            if (activities != null && activities.length > 0)
            {
                newState.activitySelectedIndex = 0;
                newState.activitySelectedData = activities.first();
            }
            else
            {
                newState.activitySelectedIndex = -1;
                newState.activitySelectedData = null;
            }

            newState.links = links;

            newState.alertMessage = null;
            newState.alertType = null;

            // Form Daten leeren.
            newState.form = {
                subject: {},
                calendar: {},
                status: {},
                displayType: {},
                timeFrom: {},
                timeTo: {},
                graveType: {},
                burialMethod: {},
                mortician: {},
                pastor: {},
                mourningHall: {},
                denomination: {}
            };

            // Form Daten setzen.
            newState.form.subject = {
                value: appointment.subject,
                selectedValue: appointment.subject_id
            };

            newState.form.calendar = {
                value: appointment.calendar_name,
                selectedValue: appointment.calendar_id
            };

            newState.form.status = {
                value: appointment.status,
                selectedValue: appointment.status_id
            };

            newState.form.displayType = {
                value: appointment.display_type_text,
                selectedValue: appointment.display_type_id
            };

            newState.form.startdate = startdate.format("L");

            newState.form.timeFrom = {
                value: startdate.format("HH:mm"),
                selectedValue: startdate.format("HH:mm")
            };

            newState.form.timeTo = {
                value: enddate.format("HH:mm"),
                selectedValue: enddate.format("HH:mm")
            };

            newState.isTimeToDisabled = false;

            newState.form.setuptimeBefore = appointment.setuptime_before ? appointment.setuptime_before : 0;
            newState.form.setuptimeAfter = appointment.setuptime_after ? appointment.setuptime_after : 0;

            newState.form.burialMethod = {
                value: appointment.burial_method,
                selectedValue: appointment.burial_method_id
            };

            newState.form.graveNo = appointment.grave_no;
            newState.form.graveType = {
                value: appointment.grave_type,
                selectedValue: appointment.grave_type
            };

            if (mortician != null)
            {
                newState.form.mortician = {
                    value: mortician.fullname_and_address_without_title_rev,
                    selectedValue: mortician.id
                };
            }

            if (pastor != null)
            {
                newState.form.pastor = {
                    value: pastor.fullname_and_address_without_title_rev,
                    selectedValue: pastor.id
                };
            }

            newState.form.mourningHall = {
                value: appointment.mourning_hall_name,
                selectedValue: appointment.mourning_hall_id
            };

            newState.form.funeralGuestsCount = appointment.funeral_guests_count ? appointment.funeral_guests_count : 0;
            newState.form.remark = appointment.remark;
            newState.form.remark2 = appointment.remark2;

            if (deceased != null)
            {
                newState.form.lastname = deceased.lastname;
                newState.form.firstname = deceased.firstname;
                newState.form.birthname = deceased.birthname;
                newState.form.dateOfBirth = moment(deceased.date_of_birth).format("L");
                newState.form.dateOfDeath = moment(deceased.deceased_date_of_death).format("L");
                newState.form.placeOfDeath = deceased.deceased_place_of_death;
                newState.form.denomination = {
                    value: deceased.deceased_denomination,
                    selectedValue: deceased.deceased_denomination_id
                };
            }

            if (customer != null)
            {
                newState.form.customerTitle = customer.title;
                newState.form.customerLastname = customer.lastname;
                newState.form.customerFirstname = customer.firstname;
                newState.form.customerStreet = customer.street;
                newState.form.customerStreetNo = customer.street_no;
                newState.form.customerCountry = customer.country;
                newState.form.customerZipCode = customer.zip_code;
                newState.form.customerCity = customer.city;
                newState.form.customerPhoneNumber = customer.phone_number;
            }

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    private async initCopyModeAsync(options: IInitOptions)
    {
        const { props, appointmentId, currentUser, settings } = options;

        // Termin aus der Datenbank laden.
        var appointment = await apiEvents.getEvent(appointmentId);

        // Momentan selektierten Kalender oder Kalendergruppe ermitteln.
        var selectedObject: modelsCalendar.ISelectedObject;
        var uri: uri.URI = this.props.currentRoute();
        var calendarId: number = uri.getQueryParameterValue<number>("calendar");
        var selectedCalendar: modelsGet.ICalendar = null;

        if (calendarId > 0)
        {
            selectedCalendar = options.calendars.where(c => c.id === calendarId).first();
        }
        else
        {
            if (props.selectedCalendar != null && props.selectedCalendar > 0)
                selectedCalendar = options.calendars.where(c => c.id === props.selectedCalendar).first();
        }

        var promise = utilsCalendar.getSelectedObject(props.selectedObject, options.calendarGroups, options.calendars);
        promise.calendar((calendar) =>
        {
            selectedObject = {
                type: enums.CalendarObjectType.calendar,
                data: calendar
            };

            if (!(calendarId > 0))
                selectedCalendar = calendar;
        });
        promise.calendarGroup((group) =>
        {
            selectedObject = {
                type: enums.CalendarObjectType.calendarGroup,
                data: group
            };
        });

        // Selektierte Labels ermitteln.
        var selectedLabels: modelsGet.ILabel[] = [];
        var popupLabels: modelsGet.ILabel[] = [];

        if (appointment != null && appointment.labels != null)
        {
            for (let eventLabel of appointment.labels)
            {
                selectedLabels.push({
                    label_id: eventLabel.label_id,
                    label: eventLabel.label,
                    label_color: eventLabel.label_color,
                    label_is_visible: eventLabel.label_is_visible,
                    label_initial: eventLabel.label_initial
                });
            }
        }

        if (selectedLabels != null && selectedLabels.length > 0)
        {
            for (let label of options.labels)
            {
                let lbl: modelsGet.ILabel = selectedLabels.where(l => l.label_id === label.label_id).first();
                if (lbl == null)
                {
                    popupLabels.push({
                        ...label
                    });
                }
            }
        }
        else
        {
            for (let label of options.labels)
            {
                popupLabels.push({
                    ...label
                });
            }
        }

        // Zur verfügung stehende Zeiten ermitteln.
        var prevUri: uri.URI = this.props.route();
        var uri: uri.URI = this.props.currentRoute();
        var viewMode: enums.CalendarViewMode = prevUri.getViewMode();
        var startdate: moment.Moment = uri.getQueryParameterValue<moment.Moment>("date");
        var enddate: moment.Moment = uri.getQueryParameterValue<moment.Moment>("enddate");
        var setuptime: number = uri.getQueryParameterValue<number>("setuptime");
        var timeFromInputValue: any;
        var timeToInputValue: any;
        var timeFrom: modelsGet.ITime[] = await apiTimes.getAvailableTime(selectedCalendar.id);
        var timeTo: modelsGet.ITime[] = [];
        if (viewMode !== enums.CalendarViewMode.month)
        {
            timeFromInputValue = {
                value: startdate.format("HH:mm"),
                selectedValue: startdate.format("HH:mm")
            };

            if (enddate != null)
            {
                timeToInputValue = {
                    value: enddate.format("HH:mm"),
                    selectedValue: enddate.format("HH:mm")
                };
            }

            timeTo = await apiTimes.getAvailableTime(selectedCalendar.id, startdate.format("HH:mm"));
        }

        // Alle Betreffzeilen ermitteln.
        var subjects: modelsGet.ISubject[] = await apiSubjects.getSubjectsByCalendarId(appointment.calendar_id);

        // Alle Grabarten ermitteln.
        var graveTypes: modelsGet.IGraveType[] = await apiGraveTypes.getGraveTypesByCalendarId(appointment.calendar_id);

        // Den im Termin zugeordneten Bestatter ermitteln.
        var mortician: modelsGet.IPerson;
        if (appointment.persons != null && appointment.persons.length > 0)
        {
            mortician = appointment.persons.where(p => p.is_mortician).first();
        }

        // Den im Termin zugeordneten Pfarrer ermitteln.
        var pastor: modelsGet.IPerson;
        if (appointment.persons != null && appointment.persons.length > 0)
        {
            pastor = appointment.persons.where(p => p.is_pastor).first();
        }

        // Den im Termin zugeordneten verstorbenen ermitteln.
        var deceased: modelsGet.IPerson;
        if (appointment.persons != null && appointment.persons.length > 0)
        {
            deceased = appointment.persons.where(p => p.is_deceased).first();
        }

        // Den im Termin zugeordneten verstorbenen ermitteln.
        var customer: modelsGet.IPerson;
        if (appointment.persons != null && appointment.persons.length > 0)
        {
            customer = appointment.persons.where(p => p.is_customer).first();
        }

        // Alle Templates ohne Listen laden.
        var templates: modelsGet.ITemplate[] = options.templates.where(t => !t.is_list);

        // Berechtigungen prüfen.
        var priviliges: IState = this.initPriviliges(currentUser, selectedCalendar, appointment);

        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.isEverythingDisabled = priviliges.isEverythingDisabled;
            newState.isStatusDisabled = priviliges.isStatusDisabled;
            newState.isTimeUserDefinable = priviliges.isTimeUserDefinable;
            newState.isEventDeletable = priviliges.isEventDeletable;
            newState.isEventPrintible = priviliges.isEventPrintible;
            newState.isInBlockMode = priviliges.isInBlockMode;
            newState.isUserLimited = priviliges.isUserLimited;
            newState.isUserAMortician = priviliges.isUserAMortician;

            newState.settings = settings;
            newState.selectedObject = selectedObject;
            newState.selectedCalendar = selectedCalendar;
            newState.currentUser = currentUser;

            newState.appointment = appointment;
            newState.selectedLabels = selectedLabels;
            newState.popupLabels = popupLabels;
            newState.subjects = subjects;
            newState.calendarGroups = options.calendarGroups;
            newState.calendars = options.calendars;
            newState.status = options.status;
            newState.displayTypes = options.displayTypes;
            newState.burialMethods = options.burialMethods;
            newState.graveTypes = graveTypes;
            newState.morticians = options.morticians;
            newState.pastors = options.pastors;
            newState.denominations = options.denominations;
            newState.labels = options.labels;
            newState.templates = templates;
            newState.mourningHalls = options.mourningHalls;
            newState.timeFrom = timeFrom;
            newState.timeTo = timeTo;

            newState.alertMessage = null;
            newState.alertType = null;

            // Form Daten leeren.
            newState.form = {
                subject: {},
                calendar: {},
                status: {},
                displayType: {},
                timeFrom: {},
                timeTo: {},
                graveType: {},
                burialMethod: {},
                mortician: {},
                pastor: {},
                mourningHall: {},
                denomination: {}
            };

            // Form Daten setzen.
            newState.form.subject = {
                value: appointment.subject,
                selectedValue: appointment.subject_id
            };

            newState.form.calendar = {
                value: selectedCalendar.name,
                selectedValue: selectedCalendar.id
            };

            newState.form.status = {
                value: appointment.status,
                selectedValue: appointment.status_id
            };

            newState.form.displayType = {
                value: appointment.display_type_text,
                selectedValue: appointment.display_type_id
            };

            newState.form.startdate = startdate.format("L");

            if (timeFromInputValue != null)
            {
                newState.form.timeFrom = timeFromInputValue;
                newState.isTimeToDisabled = false;
            }
            else
            {
                newState.isTimeToDisabled = true;
            }

            if (timeToInputValue != null)
            {
                newState.form.timeTo = timeToInputValue;
            }

            if (setuptime != null && setuptime > 0)
            {
                newState.form.setuptimeAfter = setuptime;
            }

            newState.isTimeToDisabled = false;

            newState.form.burialMethod = {
                value: appointment.burial_method,
                selectedValue: appointment.burial_method_id
            };

            newState.form.graveNo = appointment.grave_no;
            newState.form.graveType = {
                value: appointment.grave_type,
                selectedValue: appointment.grave_type
            };

            if (mortician != null)
            {
                newState.form.mortician = {
                    value: mortician.fullname_and_address_without_title_rev,
                    selectedValue: mortician.id
                };
            }

            if (pastor != null)
            {
                newState.form.pastor = {
                    value: pastor.fullname_and_address_without_title_rev,
                    selectedValue: pastor.id
                };
            }

            newState.form.mourningHall = {
                value: appointment.mourning_hall_name,
                selectedValue: appointment.mourning_hall_id
            };

            newState.form.funeralGuestsCount = appointment.funeral_guests_count ? appointment.funeral_guests_count : 0;
            newState.form.remark = appointment.remark;
            newState.form.remark2 = appointment.remark2;

            if (deceased != null)
            {
                newState.form.lastname = deceased.lastname;
                newState.form.firstname = deceased.firstname;
                newState.form.birthname = deceased.birthname;
                newState.form.dateOfBirth = moment(deceased.date_of_birth).format("L");
                newState.form.dateOfDeath = moment(deceased.deceased_date_of_death).format("L");
                newState.form.placeOfDeath = deceased.deceased_place_of_death;
                newState.form.denomination = {
                    value: deceased.deceased_denomination,
                    selectedValue: deceased.deceased_denomination_id
                };
            }

            if (customer != null)
            {
                newState.form.customerTitle = customer.title;
                newState.form.customerLastname = customer.lastname;
                newState.form.customerFirstname = customer.firstname;
                newState.form.customerStreet = customer.street;
                newState.form.customerStreetNo = customer.street_no;
                newState.form.customerCountry = customer.country;
                newState.form.customerZipCode = customer.zip_code;
                newState.form.customerCity = customer.city;
                newState.form.customerPhoneNumber = customer.phone_number;
            }

            if (timeToInputValue != null && timeToInputValue.selectedValue != null)
                this._isSaveButtonDisabled = false;

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    private async initFollowUpModeAsync(options: IInitOptions)
    {
        const { props, appointmentId, currentUser, settings } = options;

        // Termin aus der Datenbank laden.
        var appointment = await apiEvents.getEvent(appointmentId);

        // Momentan selektierten Kalender oder Kalendergruppe ermitteln.
        var selectedObject: modelsCalendar.ISelectedObject;
        var uri: uri.URI = this.props.currentRoute();
        var calendarId: number = uri.getQueryParameterValue<number>("calendar");
        var selectedCalendar: modelsGet.ICalendar = null;

        if (calendarId > 0)
        {
            selectedCalendar = options.calendars.where(c => c.id === calendarId).first();
        }
        else
        {
            if (props.selectedCalendar != null && props.selectedCalendar > 0)
                selectedCalendar = options.calendars.where(c => c.id === props.selectedCalendar).first();
        }

        var promise = utilsCalendar.getSelectedObject(props.selectedObject, options.calendarGroups, options.calendars);
        promise.calendar((calendar) =>
        {
            selectedObject = {
                type: enums.CalendarObjectType.calendar,
                data: calendar
            };

            if (!(calendarId > 0))
                selectedCalendar = calendar;
        });
        promise.calendarGroup((group) =>
        {
            selectedObject = {
                type: enums.CalendarObjectType.calendarGroup,
                data: group
            };
        });

        var popupLabels: modelsGet.ILabel[] = [];

        // Labels für das Popup ermitteln.
        if (options.labels != null && options.labels.length > 0)
        {
            for (let label of options.labels)
            {
                popupLabels.push({
                    ...label
                });
            }
        }

        // Zur verfügung stehende Zeiten ermitteln.
        var prevUri: uri.URI = this.props.route();
        var uri: uri.URI = this.props.currentRoute();
        var viewMode: enums.CalendarViewMode = prevUri.getViewMode();
        var startdate: moment.Moment = uri.getQueryParameterValue<moment.Moment>("date");
        var enddate: moment.Moment = uri.getQueryParameterValue<moment.Moment>("enddate");
        var setuptime: number = uri.getQueryParameterValue<number>("setuptime");
        var timeFromInputValue: any;
        var timeToInputValue: any;
        var timeFrom: modelsGet.ITime[] = await apiTimes.getAvailableTime(selectedCalendar.id);
        var timeTo: modelsGet.ITime[] = [];
        if (viewMode !== enums.CalendarViewMode.month)
        {
            timeFromInputValue = {
                value: startdate.format("HH:mm"),
                selectedValue: startdate.format("HH:mm")
            };

            if (enddate != null)
            {
                timeToInputValue = {
                    value: enddate.format("HH:mm"),
                    selectedValue: enddate.format("HH:mm")
                };
            }

            timeTo = await apiTimes.getAvailableTime(selectedCalendar.id, startdate.format("HH:mm"));
        }

        // Alle Betreffzeilen ermitteln.
        var subjects: modelsGet.ISubject[] = await apiSubjects.getSubjectsByCalendarId(appointment.calendar_id);
        subjects.removeAt(s => s.subject_type_id !== 2);

        // Alle Grabarten ermitteln.
        var graveTypes: modelsGet.IGraveType[] = await apiGraveTypes.getGraveTypesByCalendarId(appointment.calendar_id);

        // Den im Termin zugeordneten Bestatter ermitteln.
        var mortician: modelsGet.IPerson;
        if (appointment.persons != null && appointment.persons.length > 0)
        {
            mortician = appointment.persons.where(p => p.is_mortician).first();
        }

        // Den im Termin zugeordneten Pfarrer ermitteln.
        var pastor: modelsGet.IPerson;
        if (appointment.persons != null && appointment.persons.length > 0)
        {
            pastor = appointment.persons.where(p => p.is_pastor).first();
        }

        // Den im Termin zugeordneten verstorbenen ermitteln.
        var deceased: modelsGet.IPerson;
        if (appointment.persons != null && appointment.persons.length > 0)
        {
            deceased = appointment.persons.where(p => p.is_deceased).first();
        }

        // Den im Termin zugeordneten verstorbenen ermitteln.
        var customer: modelsGet.IPerson;
        if (appointment.persons != null && appointment.persons.length > 0)
        {
            customer = appointment.persons.where(p => p.is_customer).first();
        }

        // Alle Templates ohne Listen laden.
        var templates: modelsGet.ITemplate[] = options.templates.where(t => !t.is_list);

        // Berechtigungen prüfen.
        var priviliges: IState = this.initPriviliges(currentUser, selectedCalendar, appointment);

        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.isEverythingDisabled = priviliges.isEverythingDisabled;
            newState.isStatusDisabled = priviliges.isStatusDisabled;
            newState.isTimeUserDefinable = priviliges.isTimeUserDefinable;
            newState.isEventDeletable = priviliges.isEventDeletable;
            newState.isEventPrintible = priviliges.isEventPrintible;
            newState.isInBlockMode = priviliges.isInBlockMode;
            newState.isUserLimited = priviliges.isUserLimited;
            newState.isUserAMortician = priviliges.isUserAMortician;

            newState.settings = settings;
            newState.selectedObject = selectedObject;
            newState.selectedCalendar = selectedCalendar;
            newState.currentUser = currentUser;

            newState.appointment = appointment;
            newState.popupLabels = popupLabels;
            newState.subjects = subjects;
            newState.calendarGroups = options.calendarGroups;
            newState.calendars = options.calendars;
            newState.status = options.status;
            newState.displayTypes = options.displayTypes;
            newState.burialMethods = options.burialMethods;
            newState.graveTypes = graveTypes;
            newState.morticians = options.morticians;
            newState.pastors = options.pastors;
            newState.denominations = options.denominations;
            newState.labels = options.labels;
            newState.templates = templates;
            newState.mourningHalls = options.mourningHalls;
            newState.timeFrom = timeFrom;
            newState.timeTo = timeTo;

            newState.alertMessage = null;
            newState.alertType = null;

            // Form Daten leeren.
            newState.form = {
                subject: {},
                calendar: {},
                status: {},
                displayType: {},
                timeFrom: {},
                timeTo: {},
                graveType: {},
                burialMethod: {},
                mortician: {},
                pastor: {},
                mourningHall: {},
                denomination: {}
            };

            // Form Daten setzen.
            if (subjects.length === 1)
            {
                newState.form.subject = {
                    value: subjects.first().subject,
                    selectedValue: subjects.first().id
                };
            }
            else
            {
                newState.form.subject = {
                    value: null,
                    selectedValue: null
                };
            }

            newState.form.calendar = {
                value: selectedCalendar.name,
                selectedValue: selectedCalendar.id
            };

            newState.form.status = {
                value: appointment.status,
                selectedValue: appointment.status_id
            };

            newState.form.displayType = {
                value: appointment.display_type_text,
                selectedValue: appointment.display_type_id
            };

            newState.form.startdate = startdate.format("L");

            if (timeFromInputValue != null)
            {
                newState.form.timeFrom = timeFromInputValue;
                newState.isTimeToDisabled = false;
            }
            else
            {
                newState.isTimeToDisabled = true;
            }

            if (timeToInputValue != null)
            {
                newState.form.timeTo = timeToInputValue;
            }

            if (setuptime != null && setuptime > 0)
            {
                newState.form.setuptimeAfter = setuptime;
            }

            newState.isTimeToDisabled = false;

            newState.form.burialMethod = {
                value: appointment.burial_method,
                selectedValue: appointment.burial_method_id
            };

            newState.form.graveNo = appointment.grave_no;
            newState.form.graveType = {
                value: appointment.grave_type,
                selectedValue: appointment.grave_type
            };

            if (mortician != null)
            {
                newState.form.mortician = {
                    value: mortician.fullname_and_address_without_title_rev,
                    selectedValue: mortician.id
                };
            }

            if (pastor != null)
            {
                newState.form.pastor = {
                    value: pastor.fullname_and_address_without_title_rev,
                    selectedValue: pastor.id
                };
            }

            newState.form.mourningHall = {
                value: appointment.mourning_hall_name,
                selectedValue: appointment.mourning_hall_id
            };

            newState.form.funeralGuestsCount = appointment.funeral_guests_count ? appointment.funeral_guests_count : 0;
            newState.form.remark = appointment.remark;
            newState.form.remark2 = appointment.remark2;

            if (deceased != null)
            {
                newState.form.lastname = deceased.lastname;
                newState.form.firstname = deceased.firstname;
                newState.form.birthname = deceased.birthname;
                newState.form.dateOfBirth = moment(deceased.date_of_birth).format("L");
                newState.form.dateOfDeath = moment(deceased.deceased_date_of_death).format("L");
                newState.form.placeOfDeath = deceased.deceased_place_of_death;
                newState.form.denomination = {
                    value: deceased.deceased_denomination,
                    selectedValue: deceased.deceased_denomination_id
                };
            }

            if (customer != null)
            {
                newState.form.customerTitle = customer.title;
                newState.form.customerLastname = customer.lastname;
                newState.form.customerFirstname = customer.firstname;
                newState.form.customerStreet = customer.street;
                newState.form.customerStreetNo = customer.street_no;
                newState.form.customerCountry = customer.country;
                newState.form.customerZipCode = customer.zip_code;
                newState.form.customerCity = customer.city;
                newState.form.customerPhoneNumber = customer.phone_number;
            }

            if (timeToInputValue != null && timeToInputValue.selectedValue != null && subjects.length === 1)
                this._isSaveButtonDisabled = false;

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Prüft alle Berechtigungen eines Benutzers.
     * @param props Die übergebenen Eigenschaften.
     * @param selectedCalendar Der momentan selektierte Kalender.
     * @param appointment Der geöffnete Termin.
     */
    private initPriviliges(currentUser: modelsGet.IUser, selectedCalendar: modelsGet.ICalendar, appointment: modelsGet.IAppointment = null): IState
    {
        const settings: modelsGet.ICalendarSettings = JSON.parse(selectedCalendar.settings);
        const state: IState = {};

        // Darf der Benutzer den Termin bearbeiten oder löschen?
        if (appointment != null && appointment.id > 0)
        {
            if (appointment.is_archived)
            {
                state.isEverythingDisabled = true;

                if (currentUser.claims.can_delete_archived_event)
                    state.isEventDeletable = true;
            }
            else
            {
                if (!currentUser.claims.can_edit_event || (!currentUser.claims.can_edit_event_status_ids.contains(0) && (appointment.status_id && !currentUser.claims.can_edit_event_status_ids.contains(appointment.status_id))))
                    state.isEverythingDisabled = true;
                else
                    state.isEverythingDisabled = false;

                if (currentUser.claims.can_delete_event && (currentUser.claims.can_delete_event_status_ids.contains(0) || currentUser.claims.can_delete_event_status_ids.contains(appointment.status_id) || !appointment.status_id))
                    state.isEventDeletable = true;
            }
        }
        else
        {
            state.isEverythingDisabled = false;
        }

        // Darf der Benutzer den Status bearbeiten?
        state.isStatusDisabled = !currentUser.claims.can_edit_event_status;

        // Darf der Benutzer die Zeit selbst definieren?
        state.isTimeUserDefinable = currentUser.claims.can_create_event_freely;

        // Ist in dem Kalender die Option "Freie Blöcke" eingeschaltet und der Benutzer darf keine Zeiten selbst bestimmen?
        state.isInBlockMode = settings.time.show_free_blocks && !currentUser.claims.can_create_event_freely;

        // Darf der Benutzer nur bestimmte Felder bearbeiten?
        state.isUserLimited = currentUser.claims.can_edit_event_limited;

        // Ist der Benutzer ein Bestatter ist?
        state.isUserAMortician = currentUser.mortician_id > 0;

        // Darf der Benutzer Reports öffnen?
        if (appointment != null && appointment.id > 0)
        {
            if (appointment.status_id == null || appointment.status_id < 0)
            {
                state.isEventPrintible = true;
            }
            else if (currentUser.claims.can_print_status_ids != null)
            {
                if (currentUser.claims.can_print_status_ids.contains(0))
                {
                    state.isEventPrintible = true;
                }
                else
                {
                    state.isEventPrintible = currentUser.claims.can_print_status_ids.contains(appointment.status_id);
                }
            }
        }

        return state;
    }

    /**
     * Diese Funktion wird nur einmal beim ersten Init aufgerufen.
     */
    componentDidMount()
    {
        this._isFirstInit = true;
    }

    /**
     * Wird ausgeführt, bevor gerendert wird.
     * Hier wird geprüft ob der Button zum erstellen oder speichern eines Termins
     * freigschaltet werden soll.
     * @param nextProps Die neuen Eigenschaften.
     * @param nextState Der neue State.
     */
    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        if (this._isFirstInit)
        {
            this._isFirstInit = false;
            return;
        }

        this._isSaveButtonDisabled = true;
        this._createOnlyComment = false;

        if (this._initialState != null)
        {
            const {
                mode,
                selectedLabels,
                form
            } = this._initialState;

            // Prüfen ob sich die Labels geändert haben.
            if (selectedLabels.length !== nextState.selectedLabels.length)
            {
                this._isSaveButtonDisabled = false;
            }
            else if ((selectedLabels.length > 0 && nextState.selectedLabels.length > 0) && (!selectedLabels.any(l => nextState.selectedLabels.any(l2 => l2.label_id === l.label_id))))
            {
                this._isSaveButtonDisabled = false;
            }

            // Betreff prüfen.
            if (form.subject.selectedValue !== nextState.form.subject.selectedValue)
                this._isSaveButtonDisabled = false;

            // Kalender prüfen.
            if (form.calendar.selectedValue !== nextState.form.calendar.selectedValue)
                this._isSaveButtonDisabled = false;

            // Status prüfen.
            if (form.status.selectedValue !== nextState.form.status.selectedValue)
                this._isSaveButtonDisabled = false;

            // Anzeigetype prüfen.
            if (form.displayType.selectedValue !== nextState.form.displayType.selectedValue)
                this._isSaveButtonDisabled = false;

            // Datum prüfen.
            if (form.startdate !== nextState.form.startdate)
                this._isSaveButtonDisabled = false;

            // Zeit von prüfen.
            if (form.timeFrom.value !== nextState.form.timeFrom.value)
                this._isSaveButtonDisabled = false;

            // Zeit bis prüfen.
            if (form.timeTo.value !== nextState.form.timeTo.value)
                this._isSaveButtonDisabled = false;

            // Rüstzeit davor prüfen.
            if (form.setuptimeBefore !== nextState.form.setuptimeBefore)
                this._isSaveButtonDisabled = false;

            // Rüstzeit danach prüfen.
            if (form.setuptimeAfter !== nextState.form.setuptimeAfter)
                this._isSaveButtonDisabled = false;

            // Bestattungsart prüfen.
            if (form.burialMethod.selectedValue !== nextState.form.burialMethod.selectedValue)
                this._isSaveButtonDisabled = false;

            // Grabnr. prüfen.
            if (form.graveNo !== nextState.form.graveNo)
                this._isSaveButtonDisabled = false;

            // Grabart prüfen.
            if (form.graveType.selectedValue !== nextState.form.graveType.selectedValue)
                this._isSaveButtonDisabled = false;

            // Bestatter prüfen.
            if (form.mortician.selectedValue !== nextState.form.mortician.selectedValue)
                this._isSaveButtonDisabled = false;

            // Pfarrer prüfen.
            if (form.pastor.selectedValue !== nextState.form.pastor.selectedValue)
                this._isSaveButtonDisabled = false;

            // Trauerhalle prüfen.
            if (form.mourningHall.selectedValue !== nextState.form.mourningHall.selectedValue)
                this._isSaveButtonDisabled = false;

            // Anzahl Trauergäste prüfen.
            if (form.funeralGuestsCount !== nextState.form.funeralGuestsCount)
                this._isSaveButtonDisabled = false;

            // Bemerkung prüfen.
            if (form.remark !== nextState.form.remark)
                this._isSaveButtonDisabled = false;

            // Bemerkung2 prüfen.
            if (form.remark2 !== nextState.form.remark2)
                this._isSaveButtonDisabled = false;

            // Verstorbener: Nachname prüfen.
            if (form.lastname !== nextState.form.lastname)
                this._isSaveButtonDisabled = false;

            // Verstorbener: Vorname prüfen.
            if (form.firstname !== nextState.form.firstname)
                this._isSaveButtonDisabled = false;

            // Verstorbener: Geburtsname prüfen.
            if (form.birthname !== nextState.form.birthname)
                this._isSaveButtonDisabled = false;

            // Verstorbener: Geburtsdatum prüfen.
            if (form.dateOfBirth !== nextState.form.dateOfBirth)
                this._isSaveButtonDisabled = false;

            // Verstorbener: Sterbedatum prüfen.
            if (form.dateOfDeath !== nextState.form.dateOfDeath)
                this._isSaveButtonDisabled = false;

            // Verstorbener: Sterbeort prüfen.
            if (form.placeOfDeath !== nextState.form.placeOfDeath)
                this._isSaveButtonDisabled = false;

            // Verstorbener: Konfession prüfen.
            if (form.denomination.selectedValue !== nextState.form.denomination.selectedValue)
                this._isSaveButtonDisabled = false;

            // Auftraggeber: Anrede prüfen.
            if (form.customerTitle !== nextState.form.customerTitle)
                this._isSaveButtonDisabled = false;

            // Auftraggeber: Nachname prüfen.
            if (form.customerLastname !== nextState.form.customerLastname)
                this._isSaveButtonDisabled = false;

            // Auftraggeber: Vorname prüfen.
            if (form.customerFirstname !== nextState.form.customerFirstname)
                this._isSaveButtonDisabled = false;

            // Auftraggeber: Straße prüfen.
            if (form.customerStreet !== nextState.form.customerStreet)
                this._isSaveButtonDisabled = false;

            // Auftraggeber: Hausnummer prüfen.
            if (form.customerStreetNo !== nextState.form.customerStreetNo)
                this._isSaveButtonDisabled = false;

            // Auftraggeber: Land prüfen.
            if (form.customerCountry !== nextState.form.customerCountry)
                this._isSaveButtonDisabled = false;

            // Auftraggeber: PLZ prüfen.
            if (form.customerZipCode !== nextState.form.customerZipCode)
                this._isSaveButtonDisabled = false;

            // Auftraggeber: Ort prüfen.
            if (form.customerCity !== nextState.form.customerCity)
                this._isSaveButtonDisabled = false;

            // Auftraggeber: Rufnummer prüfen.
            if (form.customerPhoneNumber !== nextState.form.customerPhoneNumber)
                this._isSaveButtonDisabled = false;

            // Kommentar prüfen.
            if (form.comment !== nextState.form.comment)
            {
                if (mode !== Mode.create && this._isSaveButtonDisabled)
                    this._createOnlyComment = true;

                this._isSaveButtonDisabled = false;
            }
        }
    }

    /**
     * Speichert die Referenz zum DOM-Element der Kommentarbox in
     * eine globale Variable.
     * @param ref Die Referent zum DOM-Element.
     */
    private setCommentTextAreaRef = (ref: HTMLElement) =>
    {
        this._commentTextAreaRef = ref as HTMLTextAreaElement;
    }

    /**
     * Wird ausgeführt, bevor der Dialog sich schließt.
     */
    private onClosing = (refresh: boolean = false) =>
    {
        var closeDialog: boolean = true;

        if (!this._isSaveButtonDisabled)
            closeDialog = confirm("Es sind noch ungespeicherte Änderungen vorhanden. Wollen Sie den Termin trotzdem schließen?");

        if (closeDialog)
        {
            const { history, onClose } = this.props;
            history.goBack();
            onClose && onClose(refresh);
            return true;
        }
    }

    /**
     * Öffent den Dialog zum Löschen des Termins.
     */
    private onDeleteDialogClosing = (deleted: boolean) =>
    {
        if (deleted)
        {
            this.onClosing(true);
        }
        else
        {
            this.setState({
                isDeleteDialogOpen: false
            });
        }
    }

    /**
     * Fügt ein Label zur Auswahl hinzu.
     * @param label Das Label was hinzugefügt werden soll.
     */
    private onAddLabelClick = (label: modelsGet.ILabel) =>
    {
        const { selectedLabels, popupLabels } = this.state;
        const newSelectedLabels: modelsGet.ILabel[] = [];
        const newPopupLabels: modelsGet.ILabel[] = [];

        selectedLabels.forEach(l => newSelectedLabels.push({ ...l }));
        popupLabels.forEach(l => newPopupLabels.push({ ...l }));

        newSelectedLabels.push({ ...label });
        newPopupLabels.removeAt(l => l.label_id === label.label_id);

        this.setState({
            selectedLabels: newSelectedLabels,
            popupLabels: newPopupLabels
        });
    }

    /**
     * Entfernt ein Label von der Auswahl.
     * @param label Das Label was entfernt werden soll.
     */
    private onRemoveLabelClick = (label: modelsGet.ILabel) =>
    {
        const { selectedLabels, popupLabels } = this.state;
        const newSelectedLabels: modelsGet.ILabel[] = [];
        const newPopupLabels: modelsGet.ILabel[] = [];

        selectedLabels.forEach(l => newSelectedLabels.push({ ...l }));
        popupLabels.forEach(l => newPopupLabels.push({ ...l }));

        newSelectedLabels.removeAt(l => l.label_id === label.label_id);
        newPopupLabels.push({ ...label });

        this.setState({
            selectedLabels: newSelectedLabels,
            popupLabels: newPopupLabels
        });
    }

    /**
     * Ändert den geöffneten Termin oder löscht diesen.
     */
    private onSaveOrCreateClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const {
                appointment,
                mode,
                selectedLabels,
                form
            } = this.state;

            // Alle IDs selectierten Labels in ein Array schieben.
            var labelIds: number[];
            if (selectedLabels != null && selectedLabels.length > 0)
            {
                labelIds = selectedLabels.select(l => l.label_id);
            }

            // Termin Zeitraum ermitteln.
            var start: moment.Moment = undefined;
            var end: moment.Moment = undefined;
            if (utils.isValid(form.startdate, InputMask.date) && utils.isValid(form.timeFrom.value, InputMask.time) && utils.isValid(form.timeTo.value, InputMask.time)) 
            {
                var timeTo: Time = Time.parse(form.timeTo.value);
                start = moment(form.startdate + " " + form.timeFrom.value, "DD.MM.YYYY hh:mm:ss");
                end = moment(form.startdate, "DD.MM.YYYY").add(timeTo.Hours, "hours").add(timeTo.Minutes, "minutes").add(timeTo.Seconds, "seconds");
            }

            // Geburts- und Sterbedatum parsen.
            var dateOfBirth: moment.Moment = undefined;
            var dateOfDeath: moment.Moment = undefined;
            if (utils.isValid(form.dateOfBirth, InputMask.date))
            {
                dateOfBirth = moment(form.dateOfBirth, "DD.MM.YYYY");
            }

            if (utils.isValid(form.dateOfDeath, InputMask.date))
            {
                dateOfDeath = moment(form.dateOfDeath, "DD.MM.YYYY");
            }

            var newEvent: modelsPost.IEvent = {
                last_change: appointment && appointment.last_change,
                label_ids: labelIds,
                display_type_id: form.displayType.selectedValue,
                calendar_id: form.calendar.selectedValue,
                subject_id: form.subject.selectedValue,
                status_Id: form.status.selectedValue,
                starttime: (start != null) ? start.format() : undefined,
                endtime: (end != null) ? end.format() : undefined,
                setuptime_before: form.setuptimeBefore > 0 ? form.setuptimeBefore : undefined,
                setuptime_after: form.setuptimeAfter > 0 ? form.setuptimeAfter : undefined,
                remark: form.remark,
                remark2: form.remark2,
                burial_method: form.burialMethod.selectedValue,
                grave_no: form.graveNo,
                grave_type: form.graveType.selectedValue,
                mortician_id: form.mortician.selectedValue,
                pastor_id: form.pastor.selectedValue,
                mourning_hall_id: form.mourningHall.selectedValue,
                funeral_guests_count: form.funeralGuestsCount,
                comment: form.comment,
                deceased_title: form.title,
                deceased_firstname: form.firstname,
                deceased_birthname: form.birthname,
                deceased_lastname: form.lastname,
                deceased_street: form.street,
                deceased_street_no: form.streetNo,
                deceased_country: form.country,
                deceased_zip_code: form.zipCode,
                deceased_city: form.city,
                deceased_date_of_birth: (dateOfBirth != null) ? dateOfBirth.format() : undefined,
                deceased_date_of_death: (dateOfDeath != null) ? dateOfDeath.format() : undefined,
                deceased_place_of_death: form.placeOfDeath,
                deceased_denomination_id: form.denomination.selectedValue,
                customer_title: form.customerTitle,
                customer_firstname: form.customerFirstname,
                customer_lastname: form.customerLastname,
                customer_street: form.customerStreet,
                customer_street_no: form.customerStreetNo,
                customer_country: form.customerCountry,
                customer_zip_code: form.customerZipCode,
                customer_city: form.customerCity,
                customer_phone_number: form.customerPhoneNumber
            };

            // Termin erstellen oder aktualisieren.
            if (mode === Mode.create || mode === Mode.copy)
            {
                await apiEvents.post(newEvent);
            }
            else if (mode === Mode.followUp)
            {
                newEvent.parent_event_id = appointment.id;
                await apiEvents.post(newEvent);
            }
            else if (mode === Mode.edit)
            {
                if (!this._createOnlyComment)
                    await apiEvents.put(appointment.id, newEvent);
                else
                    await apiComments.post(appointment.id, form.comment);
            }

            // Save Button abschalten, damit die Meldung das der Termin geändert wurde
            // nicht mehr kommt.
            this._isSaveButtonDisabled = true;

            // Fenster schließen.
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);

            if (errors != null)
            {
                let alertMessage: string;
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                    }
                }

                this.setState(() =>
                {
                    const newState: IState = {};
                    newState.isLoading = false;
                    newState.selectedTabIndex = 0;
                    newState.errors = errors;
                    newState.alertMessage = alertMessage;
                    newState.alertType = AlertType.error;
                    return newState;
                });
            }
        }
    }

    /**
     * Lädt der Termin erneut.
     */
    private onRefreshClick(appointmentId: number)
    {
        this.setState({
            isLoading: true,
            selectedObject: null,
            appointment: null
        }, () => this.initAsync(this.props, appointmentId));
    }

    /**
     * Öffent einen Report.
     * @param template Der Report der geöffnet werden soll.
     */
    private onReportClick = (template: modelsGet.ITemplate) =>
    {
        window.open(
            `/templates?report=${template.name}&eventId=${this.state.appointment.id}`,
            "_blank"
        );
    }

    /**
     * Öffent den Dialog zum Löschen des Termins.
     */
    private onDeleteClick = () =>
    {
        this.setState({
            isDeleteDialogOpen: true
        });
    }

    /**
     * Wird ausgeführt, sobald auf ein Tab im TabControl geklickt wird.
     * @param index Der neue Tab Index.
     */
    private onTabChanged = (index: number) =>
    {
        switch (index)
        {
            case 1:
            case 2:
            case 3:
            default:
        }
        this.setState({
            selectedTabIndex: index
        });
    }

    /**
     * Wird ausgeführt, sobald auf ein Expander geklickt wird.
     * @param expander Der Expander auf den geklickt wurde.
     */
    private onExpanderClick = (expander: "general" | "deceased" | "customer") =>
    {
        switch (expander)
        {
            case "general":
                {
                    const isExpanded = this.state.isGeneralExpanded;
                    this.setState({
                        isGeneralExpanded: !isExpanded
                    });
                }
                break;
            case "deceased":
                {
                    const isExpanded = this.state.isDeceasedExpanded;
                    this.setState({
                        isDeceasedExpanded: !isExpanded
                    });
                }
                break;
            case "customer":
                {
                    const isExpanded = this.state.isCustomerExpanded;
                    this.setState({
                        isCustomerExpanded: !isExpanded
                    });
                }
                break;
        }
    }

    private onSubjectSelectionChanged = (selectedItem: modelsGet.ISubject) =>
    {
        const func = async () =>
        {
            const { subjects, form } = this.state;
            if (form.subject.selectedValue != null && form.subject.selectedValue > 0)
            {
                let subject: modelsGet.ISubject = subjects.where(s => s.id === form.subject.selectedValue).first();
                let newBefore: number = 0;
                let newAfter: number = 0;

                if (!isNaN(subject.setuptime_before) && subject.setuptime_before > 0)
                    newBefore = subject.setuptime_before;
                else
                    newBefore = form.setuptimeBefore;

                if (!isNaN(subject.setuptime_after) && subject.setuptime_after > 0)
                    newAfter = subject.setuptime_after;
                else
                    newAfter = form.setuptimeAfter;

                this.setState((state: IState) => 
                {
                    state.form.setuptimeBefore = newBefore;
                    state.form.setuptimeAfter = newAfter;
                    return state;
                });
            }
        };

        this.setState((state: IState) => 
        {
            state.form.subject = {
                value: selectedItem.subject,
                selectedValue: selectedItem.id
            };

            return state;
        }, () => func());
    }

    private onCalendarSelectionChanged = (selectedItem: modelsGet.ICalendar) =>
    {
        const func = async () =>
        {
            const { form } = this.state;
            if (form.calendar.selectedValue != null && form.calendar.selectedValue > 0)
            {
                let subjects: modelsGet.ISubject[] = await apiSubjects.getSubjectsByCalendarId(form.calendar.selectedValue);
                let graveTypes: modelsGet.IGraveType[] = await apiGraveTypes.getGraveTypesByCalendarId(form.calendar.selectedValue);
                let availableTime: modelsGet.ITime[] = await apiTimes.getAvailableTime(form.calendar.selectedValue);

                this.setState((state: IState) => 
                {
                    state.subjects = subjects;
                    state.graveTypes = graveTypes;
                    state.timeFrom = availableTime;
                    state.form.timeFrom = {};
                    state.form.timeTo = {};
                    state.isTimeToDisabled = true;
                    return state;
                });
            }
            else
            {
                this.setState((state: IState) => 
                {
                    state.isSubjectDisabled = true;
                    state.isGraveTypeDisabled = true;
                    state.isTimeToDisabled = true;
                    state.form.timeFrom = {};
                    state.form.timeTo = {};
                    return state;
                });
            }
        };

        this.setState((state: IState) => 
        {
            if (this.state.mode === Mode.edit)
            {
                state.alertMessage = "Der Kalender wurde geändert. Sind Sie sicher das Sie das wollten? Falls nicht, prüfen Sie Ihre Eingaben auf Richtigkeit. Wird ein Kalender geändert werden Felder wie die Zeitenspanne oder die Grabart neu geladen und geleert.";
                state.alertType = AlertType.warning;
            }

            state.form.calendar = {
                value: selectedItem.name,
                selectedValue: selectedItem.id
            };

            return state;
        }, () => func());
    }

    private onStatusSelectionChanged = (selectedItem: modelsGet.IStatus) =>
    {
        this.setState((state: IState) => 
        {
            state.form.status = {
                value: selectedItem ? selectedItem.status : null,
                selectedValue: selectedItem ? selectedItem.id : null
            };
            return state;
        });
    }

    private onDisplayTypeSelectionChanged = (selectedItem: modelsGet.IDisplayType) =>
    {
        this.setState((state: IState) => 
        {
            state.form.displayType = {
                value: selectedItem ? selectedItem.text : null,
                selectedValue: selectedItem ? selectedItem.id : null
            };
            return state;
        });
    }

    private onStartdateTextChanged = (value: any[], originalValue: string[]) =>
    {
        this.setState((state: IState) => 
        {
            if (this.state.mode === Mode.edit)
            {
                state.alertMessage = "Sie haben gerade das Datum geändert. Vergewissern Sie sich das der neue Eintrag wirklich richtig ist.";
                state.alertType = AlertType.warning;
            }

            state.form.startdate = value[0];

            return state;
        });
    }

    private onTimeFromTextChanged = (value: any[], originalValue: string[]) =>
    {
        this.setState((state: IState) => 
        {
            if (this.state.mode === Mode.edit)
            {
                state.alertMessage = "Sie haben gerade die Uhrzeit geändert. Vergewissern Sie sich das der neue Eintrag wirklich richtig ist.";
                state.alertType = AlertType.warning;
            }

            state.form.timeFrom = {
                value: value[0]
            };

            return state;
        }, () => this.setTimeToOptions());
    }

    private onTimeFromSelectionChanged = (selectedItem: modelsGet.ITime) =>
    {
        this.setState((state: IState) => 
        {
            if (this.state.mode === Mode.edit)
            {
                state.alertMessage = "Sie haben gerade die Uhrzeit geändert. Vergewissern Sie sich das der neue Eintrag wirklich richtig ist.";
                state.alertType = AlertType.warning;
            }

            state.form.timeFrom = {
                value: selectedItem.short,
                selectedValue: selectedItem.short
            };

            return state;
        }, () => this.setTimeToOptions());
    }

    private setTimeToOptions()
    {
        const {
            selectedCalendar,
            form
        } = this.state;

        if (Time.isValid(form.timeFrom.value))
        {
            apiTimes.getAvailableTime(selectedCalendar.id, form.timeFrom.value).done((times) =>
            {
                this.setState((state: IState) => 
                {
                    state.isTimeToDisabled = false;
                    state.timeTo = times;
                    state.form.timeTo = {};
                    return state;
                });
            });
        }
        else
        {
            this.setState((state: IState) => 
            {
                state.isTimeToDisabled = true;
                state.form.timeTo = {};
                return state;
            });
        }
    }

    private onTimeToTextChanged = (value: any[], originalValue: string[]) =>
    {
        var newValue: string = value[0];
        if (newValue != null && newValue.indexOf(" ") > 0)
            newValue = newValue.substr(0, newValue.indexOf(" "));

        this.setState((state: IState) => 
        {
            if (this.state.mode === Mode.edit)
            {
                state.alertMessage = "Sie haben gerade die Uhrzeit geändert. Vergewissern Sie sich das der neue Eintrag wirklich richtig ist.";
                state.alertType = AlertType.warning;
            }

            state.form.timeTo = {
                value: newValue
            };

            return state;
        }, () => utils.isValid(this.state.form.timeTo.value, InputMask.time) && this.calculateSetupTime());
    }

    private onTimeToSelectionChanged = (selectedItem: modelsGet.ITime) =>
    {
        this.setState((state: IState) => 
        {
            if (this.state.mode === Mode.edit)
            {
                state.alertMessage = "Sie haben gerade die Uhrzeit geändert. Vergewissern Sie sich das der neue Eintrag wirklich richtig ist.";
                state.alertType = AlertType.warning;
            }

            state.form.timeTo = {
                value: selectedItem.short,
                selectedValue: selectedItem.short
            };

            return state;
        }, () => utils.isValid(this.state.form.timeTo.value, InputMask.time) && this.calculateSetupTime());
    }

    private calculateSetupTime()
    {
        const {
            selectedCalendar,
            form
        } = this.state;
        const settings: modelsGet.ICalendarSettings = JSON.parse(selectedCalendar.settings);

        if (settings.time.automatic_setuptime)
        {
            var timeTo: Time = Time.parse(form.timeTo.value);
            var start: moment.Moment = moment(form.startdate + " " + form.timeFrom.value, "DD.MM.YYYY hh:mm:ss");
            var end: moment.Moment = moment(form.startdate, "DD.MM.YYYY").add(timeTo.Hours, "hours").add(timeTo.Minutes, "minutes").add(timeTo.Seconds, "seconds");
            var duration = moment.duration(end.diff(start));
            this.setState((state: IState) =>
            {
                state.form.setuptimeAfter = utils.calculateSetuptime(settings.time.setuptime, duration.asMinutes(), settings.time.default_interval);
                return state;
            });
        }
    }

    private onSetuptimeBeforeTextChanged = (value: any[], originalValue: string[]) =>
    {
        this.setState((state: IState) =>
        {
            state.form.setuptimeBefore = value[0] < 0 ? 0 : value[0];
            return state;
        });
    }

    private onSetuptimeAfterTextChanged = (value: any[], originalValue: string[]) =>
    {
        this.setState((state: IState) =>
        {
            state.form.setuptimeAfter = value[0] < 0 ? 0 : value[0];
            return state;
        });
    }

    private onBurialMethodSelectionChanged = (selectedItem: modelsGet.IBurialMethod) =>
    {
        this.setState((state: IState) =>
        {
            state.form.burialMethod = {
                value: selectedItem ? selectedItem.method : null,
                selectedValue: selectedItem ? selectedItem.id : null
            };
            return state;
        });
    }

    private onGraveNoTextChanged = (value: any[], originalValue: string[]) =>
    {
        this.setState((state: IState) =>
        {
            state.form.graveNo = value[0];
            return state;
        });
    }

    private onGraveTypeSelectionChanged = (selectedItem: modelsGet.IGraveType) =>
    {
        this.setState((state: IState) =>
        {
            state.form.graveType = {
                value: selectedItem ? selectedItem.type_long : null,
                selectedValue: selectedItem ? selectedItem.type_long : null
            };
            return state;
        });
    }

    private onMorticianSelectionChanged = (selectedItem: modelsGet.IPerson) =>
    {
        this.setState((state: IState) =>
        {
            state.form.mortician = {
                value: selectedItem ? selectedItem.fullname_and_address_without_title_rev : null,
                selectedValue: selectedItem ? selectedItem.id : null
            };
            return state;
        });
    }

    private onPastorSelectionChanged = (selectedItem: modelsGet.IPerson) =>
    {
        this.setState((state: IState) =>
        {
            state.form.pastor = {
                value: selectedItem ? selectedItem.fullname_and_address_without_title_rev : null,
                selectedValue: selectedItem ? selectedItem.id : null
            };
            return state;
        });
    }

    private onMourningHallSelectionChanged = (selectedItem: modelsGet.IMourningHall) =>
    {
        this.setState((state: IState) =>
        {
            state.form.mourningHall = {
                value: selectedItem ? selectedItem.name : null,
                selectedValue: selectedItem ? selectedItem.id : null
            };
            return state;
        });
    }

    private onFuneralGuestCountTextChanged = (value: any[], originalValue: string[]) =>
    {
        this.setState((state: IState) =>
        {
            state.form.funeralGuestsCount = value[0] < 0 ? 0 : value[0];
            return state;
        });
    }

    private onRemarkTextChanged = (value: any[], originalValue: string[]) =>
    {
        this.setState((state: IState) =>
        {
            state.form.remark = value[0];
            return state;
        });
    }

    private onRemark2TextChanged = (value: any[], originalValue: string[]) =>
    {
        this.setState((state: IState) =>
        {
            state.form.remark2 = value[0];
            return state;
        });
    }

    private onDeceasedTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0];
            return state;
        });
    }

    private onCustomerTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0];
            return state;
        });
    }

    private onDenominationSelectionChanged = (selectedItem: modelsGet.IDenomination) =>
    {
        this.setState((state: IState) =>
        {
            state.form.denomination = {
                value: selectedItem ? selectedItem.denomination : null,
                selectedValue: selectedItem ? selectedItem.id : null
            };
            return state;
        });
    }

    private onCommentTextChanged = (value: any[], originalValue: string[]) =>
    {
        this.setState((state: IState) =>
        {
            state.form.comment = value[0];
            return state;
        });
    }

    private onLinkRowDoubleClick = (e: ITableRowEventArgs<modelsGet.ILink>) =>
    {
        const { route, history } = this.props;
        const { selectedCalendar } = this.state;
        const uri: uri.URI = route();
        const viewMode: enums.CalendarViewMode = uri.getViewMode();

        if (viewMode === enums.CalendarViewMode.planning)
        {
            history.replace(`/event/${e.dataSource.event_id}?calendar=${e.dataSource.calendar_id}`, {
                modal: true
            });
        }
        else
        {
            history.replace(`/event/${e.dataSource.event_id}`, {
                modal: true
            });
        }

        this.onRefreshClick(e.dataSource.event_id);
    }

    private formatComment(type: "bold" | "italic" | "strikethrough" | "quote" | "code" | "link" | "bulletedList" | "numbersList"): void
    {
        this.setState((state: IState) =>
        {
            switch (type)
            {
                case "bold":
                    state.form.comment = utilsTextarea.makeTextBold(this._commentTextAreaRef) as string;
                    break;
                case "italic":
                    state.form.comment = utilsTextarea.makeTextItalic(this._commentTextAreaRef) as string;
                    break;
                case "strikethrough":
                    state.form.comment = utilsTextarea.makeTextStrikethrough(this._commentTextAreaRef) as string;
                    break;
                case "quote":
                    state.form.comment = utilsTextarea.makeQuoteBlock(this._commentTextAreaRef) as string;
                    break;
                case "code":
                    state.form.comment = utilsTextarea.makeTextCode(this._commentTextAreaRef) as string;
                    break;
                case "link":
                    state.form.comment = utilsTextarea.makeUrl(this._commentTextAreaRef) as string;
                    break;
                case "bulletedList":
                    state.form.comment = utilsTextarea.makeBulletedList(this._commentTextAreaRef) as string;
                    break;
                case "numbersList":
                    state.form.comment = utilsTextarea.makeNumberedList(this._commentTextAreaRef) as string;
                    break;
            }

            return state;
        });
    }

    /**
     * Rendert den Content Bereich des Dialogs.
     */
    private renderContent(isDeceasedExpanderVisible: boolean, isCustomerExpanderVisible: boolean): JSX.Element
    {
        const {
            isLoading,
            mode,
            appointment,
            selectedLabels,
            popupLabels,
            templates,
            lastActivity,
            isEverythingDisabled,
            isEventPrintible,
            isEventDeletable,
            selectedTabIndex,
            links
        } = this.state;

        if (!isLoading)
        {
            var isFollowUpEvent: boolean = (appointment != null ? appointment.parent_event_id > 0 : false);
            var saveButtonText: string = "";

            var title: JSX.Element[] = [];
            var labels: JSX.Element;
            var actions: JSX.Element;

            // Titel erstellen.
            if (mode === Mode.edit)
            {
                title.push(
                    <div
                        className="event-dialog_border"
                        style={{
                            borderColor: utilsCalendar.getEventColor(appointment)
                        }}></div>
                );

                title.push(
                    <div className="event-dialog_title">
                        {appointment.subject + (isFollowUpEvent ? " (Folgetermin)" : "")}
                    </div>
                );
            }
            else if (mode === Mode.create)
            {
                title.push(
                    <div className="event-dialog_title">
                        Neuen Termin erstellen
                    </div>
                );
            }
            else if (mode === Mode.copy)
            {
                title.push(
                    <div className="event-dialog_title">
                        Termin kopieren
                    </div>
                );
            }
            else if (mode === Mode.followUp)
            {
                title.push(
                    <div className="event-dialog_title">
                        Folgetermin erstellen
                    </div>
                );
            }

            // Labels erstellen.
            labels = (
                <div className="event-dialog_labels">
                    {
                        selectedLabels.map((label) =>
                        {
                            return (
                                <Label
                                    dataSource={label}
                                    text={label.label}
                                    color={label.label_color}
                                    isButtonVisible={!isEverythingDisabled}
                                    onButtonClick={this.onRemoveLabelClick} />
                            );
                        })
                    }
                    {
                        !isEverythingDisabled &&
                        <PopupProvider
                            id="labels-popup"
                            trigger={PopupTrigger.Click}>
                            <div className="event-dialog_labels_add-btn">
                                {selectedLabels.length > 0 ? <i className="mdi mdi-plus"></i> : <span>Label hinzufügen</span>}
                            </div>
                        </PopupProvider>
                    }
                </div>
            );

            switch (mode)
            {
                case Mode.create:
                    saveButtonText = "Termin erstellen";
                    break;
                case Mode.edit:
                    saveButtonText = "Speichern";
                    break;
                case Mode.copy:
                    saveButtonText = "Kopie erstellen";
                    break;
                case Mode.followUp:
                    saveButtonText = "Folgetermin erstellen";
                    break;
            }

            let disableReports: boolean = isEverythingDisabled;;
            if (isEventPrintible && (templates || []).length > 0)
                disableReports = false;
            else if (!isEventPrintible)
                disableReports = true;
            else if (!disableReports && (templates || []).length === 0)
                disableReports = true;

            actions = (
                <div className="event-dialog_actions">
                    <Button
                        size={ButtonSize.small}
                        text={saveButtonText}
                        type={ButtonType.primary}
                        isDisabled={isEverythingDisabled || this._isSaveButtonDisabled}
                        onClick={this.onSaveOrCreateClick} />

                    {mode !== Mode.create && mode !== Mode.copy && mode !== Mode.followUp && (
                        <Button
                            title="Aktualisieren"
                            size={ButtonSize.small}
                            icon="refresh"
                            type={ButtonType.transparent}
                            isDisabled={isEverythingDisabled}
                            onClick={() => this.onRefreshClick(appointment.id)} />
                    )}

                    {mode !== Mode.create && mode !== Mode.copy && mode !== Mode.followUp && (
                        <PopupProvider
                            id="printer-popup"
                            trigger={PopupTrigger.Click}
                            refPropName="buttonRef"
                            isDisabled={disableReports}>
                            <Button
                                title={`Drucken ${(templates || []).length === 0 ? " - Keine Templates vorhanden" : ""}`}
                                size={ButtonSize.small}
                                icon="printer"
                                type={ButtonType.transparent}
                                isDisabled={disableReports} />
                        </PopupProvider>
                    )}

                    {mode !== Mode.create && mode !== Mode.copy && mode !== Mode.followUp && (
                        <PopupProvider
                            id="more-actions-popup"
                            trigger={PopupTrigger.Click}
                            refPropName="buttonRef">
                            <Button
                                title="Weitere Aktionen"
                                size={ButtonSize.small}
                                icon="dots-horizontal"
                                type={ButtonType.transparent} />
                        </PopupProvider>
                    )}
                </div>
            );

            var popupLabelsComp: JSX.Element[] = [];
            if (popupLabels.length > 0)
            {
                for (let label of popupLabels)
                {
                    popupLabelsComp.push(
                        <Label
                            dataSource={label}
                            text={label.label}
                            color={label.label_color}
                            onClick={this.onAddLabelClick} />
                    );
                }
            }
            else
            {
                if (this.state.labels.length > 0)
                {
                    popupLabelsComp.push(
                        <span>
                            Es stehen keine Labels mehr zur Auswahl.
                        </span>
                    );
                }
                else
                {
                    popupLabelsComp.push(
                        <span>
                            Es stehen keine Labels zur Auswahl. Fügen Sie Labels in den Einstellungen hinzu.
                        </span>
                    );
                }
            }

            return (
                <div className="event-dialog">
                    {title}
                    {labels}
                    {actions}
                    <div className="event-dialog_infos">
                        <div className="event-dialog_infos_activity">{lastActivity}</div>
                    </div>
                    <div className="event-dialog_tabcontrol">
                        <TabControl
                            style={{
                                height: "100%"
                            }}
                            classNameTabPages="event-dialog_tabs"
                            classNameTabPanels="event-dialog_tabpanels"
                            defaultIndex={0}
                            selectedIndex={selectedTabIndex}
                            onTabChanged={this.onTabChanged}>
                            <Tab text="Details">
                                {this.renderDetails(isDeceasedExpanderVisible, isCustomerExpanderVisible)}
                            </Tab>

                            {mode === Mode.edit && (
                                <Tab
                                    icon="comment-multiple-outline"
                                    text={(appointment && appointment.comments && appointment.comments.length > 0) ? "(" + appointment.comments.length + ")" : ""}>
                                    {this.renderComments()}
                                </Tab>
                            )}

                            {mode === Mode.edit && (
                                <Tab icon="history">
                                    {this.renderActivity()}
                                </Tab>
                            )}

                            {mode === Mode.edit && (
                                <Tab
                                    icon="link-variant"
                                    text={(links && links.length > 0) ? "(" + links.length + ")" : ""}>
                                    {this.renderLinks()}
                                </Tab>
                            )}
                        </TabControl>
                    </div>
                    <Popup
                        id="labels-popup"
                        align={PopupAlign.targetNodeBottomLeft}>
                        <div className="po-labels">
                            <ScrollViewer
                                style={{
                                    position: "relative",
                                    padding: 10,
                                    paddingRight: 0
                                }}
                                styleWhenNoScrollBar={{
                                    position: "relative",
                                    padding: 10
                                }}
                                HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                                VerticalScrollBarVisibility={ScrollBarVisibility.auto}>
                                {popupLabelsComp}
                            </ScrollViewer>
                        </div>
                    </Popup>
                    <Popup
                        id="printer-popup"
                        align={PopupAlign.targetNodeBottomRight}>
                        <ContextMenu>
                            {
                                templates.map((template) =>
                                {
                                    return (
                                        <MenuItem
                                            dataSource={template}
                                            icon="file-document"
                                            header={`${template.display_name}...`}
                                            onClick={this.onReportClick} />
                                    )
                                })
                            }
                        </ContextMenu>
                    </Popup>
                    <Popup
                        id="more-actions-popup"
                        align={PopupAlign.targetNodeBottomRight}>
                        <ContextMenu>
                            <ContextMenu>
                                <MenuItem
                                    icon="delete"
                                    iconColor="#e22910"
                                    header="Löschen..."
                                    isDisabled={!isEventDeletable}
                                    onClick={this.onDeleteClick} />
                            </ContextMenu>
                        </ContextMenu>
                    </Popup>
                </div>
            );
        }

        return <Loader loaderStyle={LoaderStyle.style1} />
    }

    /**
     * Rendert den Content des Tabs "Details".
     */
    private renderDetails(isDeceasedExpanderVisible: boolean, isCustomerExpanderVisible: boolean): JSX.Element
    {
        const {
            mode,
            errors,
            alertMessage,
            alertType
        } = this.state;

        return (
            <div className="event-dialog_content_details">
                <Alert
                    style={utils.getGridChildStyle(1, 3, 1, 1)}
                    alertType={alertType}
                    isVisible={alertMessage ? true : false}
                    isClosable={true}
                    onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                    {alertMessage}
                </Alert>
                {this.renderDetailsGeneralExpander(isDeceasedExpanderVisible, isCustomerExpanderVisible)}
                {this.renderDetailsDeceasedExpander(isDeceasedExpanderVisible, isCustomerExpanderVisible)}
                {this.renderDetailsCustomerExpander(isDeceasedExpanderVisible, isCustomerExpanderVisible)}
            </div>
        );
    }

    /**
     * Rendert den Inhalt des Expanders "Allgemein".
     */
    private renderDetailsGeneralExpander(isDeceasedExpanderVisible: boolean, isCustomerExpanderVisible: boolean): JSX.Element
    {
        const {
            currentUser,
            isEverythingDisabled,
            isSubjectDisabled,
            isStatusDisabled,
            isGraveTypeDisabled,
            isTimeToDisabled,
            isTimeUserDefinable,
            isInBlockMode,
            isUserAMortician,
            isGeneralExpanded,
            settings,
            errors,
            subjects,
            calendars,
            status,
            displayTypes,
            burialMethods,
            graveTypes,
            morticians,
            pastors,
            mourningHalls,
            timeFrom,
            timeTo,
            form
        } = this.state;

        return (
            <Expander
                style={{
                    ...utils.getGridChildStyle(1, (!isDeceasedExpanderVisible && !isCustomerExpanderVisible) ? 3 : 1, 2, 4),
                    marginRight: 10
                }}
                headerText="Allgemein"
                isExpanded={isGeneralExpanded}
                onClick={() => this.onExpanderClick("general")}>
                <ScrollViewer
                    style={{
                        top: 30,
                        paddingRight: 5
                    }}
                    styleWhenNoScrollBar={{
                        paddingRight: 0
                    }}
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.visible}>
                    <FormGroup>
                        {settings.eventFields.contains("Betreff") &&
                            <ComboBox
                                label="Betreff"
                                dataSource={subjects}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.subject.value}
                                selectedValue={form.subject.selectedValue}
                                onSelectionChanged={this.onSubjectSelectionChanged}
                                valueMember="id"
                                displayMember="subject"
                                isDisabled={isEverythingDisabled || isSubjectDisabled || currentUser.claims.event_fields.contains("Betreff")}
                                errorMessage={errors["SubjectId"]}>
                                {
                                    (item: modelsGet.ISubject) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.subject} />
                                        );
                                    }
                                }
                            </ComboBox>
                        }
                        {settings.eventFields.contains("Kalender") &&
                            <ComboBox
                                label="Kalender"
                                dataSource={calendars}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.calendar.value}
                                selectedValue={form.calendar.selectedValue}
                                onSelectionChanged={this.onCalendarSelectionChanged}
                                valueMember="id"
                                displayMember="name"
                                isDisabled={isEverythingDisabled || isInBlockMode || currentUser.claims.event_fields.contains("Kalender")}
                                errorMessage={errors["CalendarId"]}>
                                {
                                    (item: modelsGet.ICalendar) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.name} />
                                        );
                                    }
                                }
                            </ComboBox>
                        }
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        {!isStatusDisabled && settings.eventFields.contains("Status") &&
                            <ComboBox
                                canRemoveSelection={true}
                                label="Status"
                                dataSource={status}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.status.value}
                                selectedValue={form.status.selectedValue}
                                onSelectionChanged={this.onStatusSelectionChanged}
                                valueMember="id"
                                displayMember="status"
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Status")}
                                errorMessage={errors["StatusId"]}>
                                {
                                    (item: modelsGet.IStatus) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.status} />
                                        );
                                    }
                                }
                            </ComboBox>
                        }
                        {settings.eventFields.contains("Anzeigetyp") &&
                            <ComboBox
                                canRemoveSelection={true}
                                label="Anzeigetyp"
                                dataSource={displayTypes}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.displayType.value}
                                selectedValue={form.displayType.selectedValue}
                                onSelectionChanged={this.onDisplayTypeSelectionChanged}
                                valueMember="id"
                                displayMember="text"
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Anzeigetyp")}
                                errorMessage={errors["DisplayTypeId"]}>
                                {
                                    (item: modelsGet.IDisplayType) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.text} />
                                        );
                                    }
                                }
                            </ComboBox>
                        }
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        {settings.eventFields.contains("Startdatum") &&
                            <TextBox
                                label="Startdatum"
                                mask={InputMask.date}
                                size={InputSize.small}
                                value={form.startdate}
                                onTextChange={this.onStartdateTextChanged}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Startdatum")}
                                errorMessage={errors["Starttime"]}/>
                        }
                        {settings.eventFields.contains("Zeit von") &&
                            <ComboBox
                                label="Zeit von"
                                mask={InputMask.time}
                                dataSource={timeFrom}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={isTimeUserDefinable ? ComboBoxType.dropDown : ComboBoxType.dropDownList}
                                value={form.timeFrom.value}
                                selectedValue={form.timeFrom.selectedValue}
                                onTextChange={this.onTimeFromTextChanged}
                                onSelectionChanged={this.onTimeFromSelectionChanged}
                                valueMember="short"
                                displayMember="short"
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Zeit von")}
                                errorMessage={errors["TimeFrom"]}>
                                {
                                    (item: modelsGet.ITime) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.short}
                                                value={item.short}
                                                displayValue={item.short} />
                                        );
                                    }
                                }
                            </ComboBox>
                        }
                        {settings.eventFields.contains("Zeit bis") &&
                            <ComboBox
                                label="Zeit bis"
                                mask={InputMask.time}
                                dataSource={timeTo}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={isTimeUserDefinable ? ComboBoxType.dropDown : ComboBoxType.dropDownList}
                                value={form.timeTo.value}
                                selectedValue={form.timeTo.selectedValue}
                                onTextChange={this.onTimeToTextChanged}
                                onSelectionChanged={this.onTimeToSelectionChanged}
                                valueMember="short"
                                displayMember="long"
                                isDisabled={isEverythingDisabled || isTimeToDisabled || currentUser.claims.event_fields.contains("Zeit bis")}
                                errorMessage={errors["TimeTo"]}>
                                {
                                    (item: modelsGet.ITime) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.short}
                                                value={item.short}
                                                displayValue={item.long} />
                                        );
                                    }
                                }
                            </ComboBox>
                        }
                        {settings.eventFields.contains("Rüstzeit davor") &&
                            <TextBox
                                label="Rüstzeit davor"
                                mask={InputMask.number}
                                size={InputSize.small}
                                valueType={InputValueType.number}
                                value={form.setuptimeBefore > 0 ? form.setuptimeBefore.toString() : "0"}
                                onTextChange={this.onSetuptimeBeforeTextChanged}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Rüstzeit davor")}
                                errorMessage={errors["SetuptimeBefore"]} />
                        }
                        {settings.eventFields.contains("Rüstzeit danach") &&
                            <TextBox
                                label="Rüstzeit danach"
                                mask={InputMask.number}
                                size={InputSize.small}
                                valueType={InputValueType.number}
                                value={form.setuptimeAfter > 0 ? form.setuptimeAfter.toString() : "0"}
                            onTextChange={this.onSetuptimeAfterTextChanged}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Rüstzeit danach")}
                                errorMessage={errors["SetuptimeAfter"]} />
                        }
                    </FormGroup>
                    <FormGroup>
                        {settings.eventFields.contains("Bestattungsart") &&
                            <ComboBox
                                canRemoveSelection={true}
                                label="Bestattungsart"
                                dataSource={burialMethods}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.burialMethod.value}
                                selectedValue={form.burialMethod.selectedValue}
                                onSelectionChanged={this.onBurialMethodSelectionChanged}
                                valueMember="id"
                                displayMember="method"
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Bestattungsart")}
                                errorMessage={errors["BurialMethod"]}>
                                {
                                    (item: modelsGet.IBurialMethod) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.method} />
                                        );
                                    }
                                }
                            </ComboBox>
                        }
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        {settings.eventFields.contains("Grabnummer") &&
                            <TextBox
                                label="Grabnummer"
                                size={InputSize.small}
                                value={form.graveNo}
                                onTextChange={this.onGraveNoTextChanged}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Grabnummer")}
                                errorMessage={errors["GraveNo"]} />
                        }
                        {settings.eventFields.contains("Grabart") &&
                            <ComboBox
                                canRemoveSelection={true}
                                label="Grabart"
                                dataSource={graveTypes}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.graveType.value}
                                selectedValue={form.graveType.selectedValue}
                                onSelectionChanged={this.onGraveTypeSelectionChanged}
                                valueMember="type_long"
                                displayMember="type_long"
                                isDisabled={isEverythingDisabled || isGraveTypeDisabled || currentUser.claims.event_fields.contains("Grabart")}
                                errorMessage={errors["GraveType"]}>
                                {
                                    (item: modelsGet.IGraveType) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.type_long}
                                                displayValue={item.type_long} />
                                        );
                                    }
                                }
                            </ComboBox>
                        }
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        {settings.eventFields.contains("Bestatter") &&
                            <ComboBox
                                canRemoveSelection={true}
                                label="Bestatter"
                                dataSource={morticians}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.mortician.value}
                                selectedValue={form.mortician.selectedValue}
                                onSelectionChanged={this.onMorticianSelectionChanged}
                                valueMember="id"
                                displayMember="fullname_and_address_without_title_rev"
                                isDisabled={isEverythingDisabled || isUserAMortician || currentUser.claims.event_fields.contains("Bestatter")}
                                errorMessage={errors["Mortician"]}>
                                {
                                    (item: modelsGet.IPerson) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.fullname_and_address_without_title_rev} />
                                        );
                                    }
                                }
                            </ComboBox>
                        }
                        {settings.eventFields.contains("Pfarrer") &&
                            <ComboBox
                                canRemoveSelection={true}
                                label="Pfarrer"
                                dataSource={pastors}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.pastor.value}
                                selectedValue={form.pastor.selectedValue}
                                onSelectionChanged={this.onPastorSelectionChanged}
                                valueMember="id"
                                displayMember="fullname_and_address_without_title_rev"
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Pfarrer")}
                                errorMessage={errors["Pastor"]}>
                                {
                                    (item: modelsGet.IPerson) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.fullname_and_address_without_title_rev} />
                                        );
                                    }
                                }
                            </ComboBox>
                        }
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        {settings.eventFields.contains("Trauerhalle") &&
                            <ComboBox
                                canRemoveSelection={true}
                                label="Trauerhalle"
                                dataSource={mourningHalls}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.mourningHall.value}
                                selectedValue={form.mourningHall.selectedValue}
                                onSelectionChanged={this.onMourningHallSelectionChanged}
                                valueMember="id"
                                displayMember="name"
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Trauerhalle")}
                                errorMessage={errors["MourningHallId"]}>
                                {
                                    (item: modelsGet.IMourningHall) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.name} />
                                        );
                                    }
                                }
                            </ComboBox>
                        }
                        {settings.eventFields.contains("Anzahl Trauergäste") &&
                            <TextBox
                                label="Anzahl Trauergäste"
                                mask={InputMask.number}
                                size={InputSize.small}
                                valueType={InputValueType.number}
                                value={form.funeralGuestsCount > 0 ? form.funeralGuestsCount.toString() : "0"}
                                onTextChange={this.onFuneralGuestCountTextChanged}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Anzahl Trauergäste")}
                                errorMessage={errors["FuneralGuestsCount"]} />
                        }
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        {settings.eventFields.contains("Bemerkung") &&
                            <TextArea
                                label="Bemerkung"
                                value={form.remark}
                                onTextChange={this.onRemarkTextChanged}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Bemerkung")}
                                errorMessage={errors["Remark"]} />
                        }
                        {settings.eventFields.contains("Bemerkung 2") &&
                            <TextArea
                                label="Bemerkung 2"
                                value={form.remark2}
                                onTextChange={this.onRemark2TextChanged}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Bemerkung 2")}
                                errorMessage={errors["Remark2"]} />
                        }
                    </FormGroup>
                </ScrollViewer>
            </Expander>
        );
    }

    /**
     * Rendert den Inhalt des Expanders "Verstorbene Person".
     */
    private renderDetailsDeceasedExpander(isDeceasedExpanderVisible: boolean, isCustomerExpanderVisible: boolean): JSX.Element
    {
        const {
            currentUser,
            isEverythingDisabled,
            isDeceasedExpanded,
            mode,
            denominations,
            errors,
            form,
            settings
        } = this.state;
        const isFollowUpMode: boolean = mode === Mode.followUp;

        if (!isDeceasedExpanderVisible)
            return null;

        return (
            <Expander
                style={{
                    ...utils.getGridChildStyle(2, 2, 2, isCustomerExpanderVisible ? 2 : 4),
                    marginLeft: 10,
                    marginBottom: 5
                }}
                headerText="Verstorbene Person"
                isExpanded={isDeceasedExpanded}
                onClick={() => this.onExpanderClick("deceased")}>
                <ScrollViewer
                    style={{
                        top: 30,
                        paddingRight: 5
                    }}
                    styleWhenNoScrollBar={{
                        paddingRight: 0
                    }}
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.visible}>
                    <FormGroup isHorizontal={true}>
                        {settings.eventFields.contains("Nachname (Verstorbener)") &&
                            <TextBox
                                label="Nachname"
                                size={InputSize.small}
                                value={form.lastname}
                                onTextChange={(value, originalValue) => this.onDeceasedTextChanged(value, originalValue, "lastname")}
                                isDisabled={isEverythingDisabled || isFollowUpMode || currentUser.claims.event_fields.contains("Nachname (Verstorbener)")}
                                errorMessage={errors["DeceasedLastname"]} />
                        }
                        {settings.eventFields.contains("Vorname (Verstorbener)") &&
                            <TextBox
                                label="Vorname"
                                size={InputSize.small}
                                value={form.firstname}
                                onTextChange={(value, originalValue) => this.onDeceasedTextChanged(value, originalValue, "firstname")}
                                isDisabled={isEverythingDisabled || isFollowUpMode || currentUser.claims.event_fields.contains("Vorname (Verstorbener)")}
                                errorMessage={errors["DeceasedFirstname"]} />
                        }
                    </FormGroup>
                    <FormGroup>
                        {settings.eventFields.contains("Geburtsname (Verstorbener)") &&
                            <TextBox
                                label="Geburtsname"
                                size={InputSize.small}
                                value={form.birthname}
                                onTextChange={(value, originalValue) => this.onDeceasedTextChanged(value, originalValue, "birthname")}
                                isDisabled={isEverythingDisabled || isFollowUpMode || currentUser.claims.event_fields.contains("Geburtsname (Verstorbener)")}
                                errorMessage={errors["DeceasedBirthname"]} />
                        }
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        {settings.eventFields.contains("Geburtsdatum (Verstorbener)") &&
                            <TextBox
                                label="Geburtsdatum"
                                mask={InputMask.date}
                                size={InputSize.small}
                                value={form.dateOfBirth}
                                onTextChange={(value, originalValue) => this.onDeceasedTextChanged(value, originalValue, "dateOfBirth")}
                                isDisabled={isEverythingDisabled || isFollowUpMode || currentUser.claims.event_fields.contains("Geburtsdatum (Verstorbener)")}
                                errorMessage={errors["DeceasedDateOfBirth"]} />
                        }
                        {settings.eventFields.contains("Sterbedatum (Verstorbener)") &&
                            <TextBox
                                label="Sterbedatum"
                                mask={InputMask.date}
                                size={InputSize.small}
                                value={form.dateOfDeath}
                                onTextChange={(value, originalValue) => this.onDeceasedTextChanged(value, originalValue, "dateOfDeath")}
                                isDisabled={isEverythingDisabled || isFollowUpMode || currentUser.claims.event_fields.contains("Sterbedatum (Verstorbener)")}
                                errorMessage={errors["DeceasedDateOfDeath"]} />
                        }
                        {settings.eventFields.contains("Sterbeort (Verstorbener)") &&
                            <TextBox
                                label="Sterbeort"
                                size={InputSize.small}
                                value={form.placeOfDeath}
                                onTextChange={(value, originalValue) => this.onDeceasedTextChanged(value, originalValue, "placeOfDeath")}
                                isDisabled={isEverythingDisabled || isFollowUpMode || currentUser.claims.event_fields.contains("Sterbeort (Verstorbener)")}
                                errorMessage={errors["DeceasedPlaceOfDeath"]} />
                        }
                    </FormGroup>
                    <FormGroup>
                        {settings.eventFields.contains("Konfession (Verstorbener)") &&
                            <ComboBox
                                canRemoveSelection={true}
                                label="Konfession"
                                dataSource={denominations}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.denomination.value}
                                selectedValue={form.denomination.selectedValue}
                                onSelectionChanged={this.onDenominationSelectionChanged}
                                valueMember="id"
                                displayMember="denomination"
                                isDisabled={isEverythingDisabled || isFollowUpMode || currentUser.claims.event_fields.contains("Konfession (Verstorbener)")}
                                errorMessage={errors["DeceasedDenominationId"]}>
                                {
                                    (item: modelsGet.IDenomination) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.denomination} />
                                        );
                                    }
                                }
                            </ComboBox>
                        }
                    </FormGroup>
                </ScrollViewer>
            </Expander>
        );
    }

    /**
     * Rendert den Inhalt des Expanders "Auftraggeber".
     */
    private renderDetailsCustomerExpander(isDeceasedExpanderVisible: boolean, isCustomerExpanderVisible: boolean): JSX.Element
    {
        const {
            currentUser,
            isEverythingDisabled,
            isCustomerExpanded,
            errors,
            form,
            settings
        } = this.state;

        if (!isCustomerExpanderVisible)
            return null;

        return (
            <Expander
                style={{
                    ...utils.getGridChildStyle(2, 2, isDeceasedExpanderVisible ? 3 : 2, isDeceasedExpanderVisible ? 3 : 4),
                    marginLeft: 10,
                    marginTop: 5
                }}
                headerText="Auftraggeber"
                isExpanded={isCustomerExpanded}
                onClick={() => this.onExpanderClick("customer")}>
                <ScrollViewer
                    style={{
                        top: 30,
                        paddingRight: 5
                    }}
                    styleWhenNoScrollBar={{
                        paddingRight: 0
                    }}
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.visible}>
                    <FormGroup>
                        {settings.eventFields.contains("Anrede (Ansprechpartner)") &&
                            <TextBox
                                label="Anrede"
                                size={InputSize.small}
                                value={form.customerTitle}
                                onTextChange={(value, originalValue) => this.onCustomerTextChanged(value, originalValue, "customerTitle")}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Anrede (Ansprechpartner)")}
                                errorMessage={errors["CustomerTitle"]} />
                        }
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        {settings.eventFields.contains("Nachname (Ansprechpartner)") &&
                            <TextBox
                                label="Nachname"
                                size={InputSize.small}
                                value={form.customerLastname}
                                onTextChange={(value, originalValue) => this.onCustomerTextChanged(value, originalValue, "customerLastname")}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Nachname (Ansprechpartner)")}
                                errorMessage={errors["CustomerLastname"]} />
                        }
                        {settings.eventFields.contains("Vorname (Ansprechpartner)") &&
                            <TextBox
                                label="Vorname"
                                size={InputSize.small}
                                value={form.customerFirstname}
                                onTextChange={(value, originalValue) => this.onCustomerTextChanged(value, originalValue, "customerFirstname")}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Vorname (Ansprechpartner)")}
                                errorMessage={errors["CustomerFirstname"]} />
                        }
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        {settings.eventFields.contains("Straße (Ansprechpartner)") &&
                            <TextBox
                                label="Straße"
                                size={InputSize.small}
                                value={form.customerStreet}
                                onTextChange={(value, originalValue) => this.onCustomerTextChanged(value, originalValue, "customerStreet")}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Straße (Ansprechpartner)")}
                                errorMessage={errors["CustomerStreet"]} />
                        }
                        {settings.eventFields.contains("Hausnummer (Ansprechpartner)") &&
                            <TextBox
                                label="Hausnummer"
                                size={InputSize.small}
                                value={form.customerStreetNo}
                                onTextChange={(value, originalValue) => this.onCustomerTextChanged(value, originalValue, "customerStreetNo")}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Hausnummer (Ansprechpartner)")}
                                errorMessage={errors["CustomerStreetNo"]} />
                        }
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        {settings.eventFields.contains("Land (Ansprechpartner)") &&
                            <TextBox
                                label="Land"
                                size={InputSize.small}
                                value={form.customerCountry}
                                onTextChange={(value, originalValue) => this.onCustomerTextChanged(value, originalValue, "customerCountry")}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Land (Ansprechpartner)")}
                                errorMessage={errors["CustomerCountry"]} />
                        }
                        {settings.eventFields.contains("PLZ (Ansprechpartner)") &&
                            <TextBox
                                label="PLZ"
                                size={InputSize.small}
                                value={form.customerZipCode}
                                onTextChange={(value, originalValue) => this.onCustomerTextChanged(value, originalValue, "customerZipCode")}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("PLZ (Ansprechpartner)")}
                                errorMessage={errors["CustomerZipCode"]} />
                        }
                        {settings.eventFields.contains("Ort (Ansprechpartner)") &&
                            <TextBox
                                label="Ort"
                                size={InputSize.small}
                                value={form.customerCity}
                                onTextChange={(value, originalValue) => this.onCustomerTextChanged(value, originalValue, "customerCity")}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Ort (Ansprechpartner)")}
                                errorMessage={errors["CustomerCity"]} />
                        }
                    </FormGroup>
                    <FormGroup>
                        {settings.eventFields.contains("Rufnummer (Ansprechpartner)") &&
                            <TextBox
                                label="Rufnummer"
                                size={InputSize.small}
                                value={form.customerPhoneNumber}
                                onTextChange={(value, originalValue) => this.onCustomerTextChanged(value, originalValue, "customerPhoneNumber")}
                                isDisabled={isEverythingDisabled || currentUser.claims.event_fields.contains("Rufnummer (Ansprechpartner)")}
                                errorMessage={errors["CustomerPhoneNumber"]} />
                        }
                    </FormGroup>
                </ScrollViewer>
            </Expander>
        );
    }

    /**
     * Rendert den Content des Tabs "Kommentare".
     */
    private renderComments(): JSX.Element
    {
        const { currentUser, appointment, form } = this.state;
        const comments: JSX.Element[] = [];
        const actions: JSX.Element[] = [];

        if (appointment.comments != null && appointment.comments.length > 0)
        {
            for (let cmt of appointment.comments)
            {
                let belongsToCurrentUser: boolean = currentUser.id === cmt.user_id;
                let isCreator: boolean = appointment.user_id === cmt.user_id;

                // CSS Klassen für comment container.
                let classNames: string[] = ["comment"];

                if (belongsToCurrentUser)
                    classNames.push("comment--belongs-to-current-user");

                // CSS Klassen für comment content.
                let contentClassNames: string[] = ["comment_content"];

                if (isCreator)
                    contentClassNames.push("comment_content--is-creator");

                comments.push(
                    <div className={classNames.join(" ")}>
                        {!belongsToCurrentUser && (
                            <img
                                className="comment_profile-picture"
                                src={cmt.user_avatar} />
                        )}
                        <div className={contentClassNames.join(" ")}>
                            <div
                                className="comment_content_message markdown"
                                dangerouslySetInnerHTML={{ __html: marked(cmt.comment) }}>
                            </div>
                            <div className="comment_content_details">
                                <span className="comment_content_details_user">{!belongsToCurrentUser ? cmt.user_fullname : ""}</span>
                                <time
                                    className="comment_content_details_time"
                                    dateTime={moment(cmt.create_date).format()}
                                    title={moment(cmt.create_date).format("LLLL")}>
                                    {moment(cmt.create_date).fromNow()}
                                </time>
                            </div>
                        </div>
                    </div>
                );
            }
        }

        actions.push(
            <ToolBar
                style={utils.getGridChildStyle(1, 1, 1, 1)}
                hasTopBorder={false}
                hasBottomBorder={false}>
                <ToolBarItem
                    title="Fettgedruckt"
                    icon="format-bold"
                    onClick={() => this.formatComment("bold")} />
                <ToolBarItem
                    title="Kursiv"
                    icon="format-italic"
                    onClick={() => this.formatComment("italic")} />
                <ToolBarItem
                    title="Durchgestrichen"
                    icon="format-strikethrough"
                    onClick={() => this.formatComment("strikethrough")} />
                <ToolBarSeparator />
                <ToolBarItem
                    title="Zitat einfügen"
                    icon="format-quote-open"
                    onClick={() => this.formatComment("quote")} />
                <ToolBarItem
                    title="Code einfügen"
                    icon="code-tags"
                    onClick={() => this.formatComment("code")} />
                <ToolBarItem
                    title="Link einfügen"
                    icon="link"
                    onClick={() => this.formatComment("link")} />
                <ToolBarSeparator />
                <ToolBarItem
                    title="Aufzählung einfügen"
                    icon="format-list-bulleted"
                    onClick={() => this.formatComment("bulletedList")} />
                <ToolBarItem
                    title="Nummerierung einfügen"
                    icon="format-list-numbers"
                    onClick={() => this.formatComment("numbersList")} />
            </ToolBar>
        );

        actions.push(
            <TextArea
                style={utils.getGridChildStyle(1, 1, 2, 2)}
                inputRef={this.setCommentTextAreaRef}
                placeholder="Fügen Sie einen Kommentar hinzu."
                value={form.comment}
                onTextChange={this.onCommentTextChanged} />
        );

        return (
            <div className="event-dialog_content_comments">
                <ScrollViewer
                    style={{
                        position: "relative",
                        ...utils.getGridChildStyle(1, 1, 1, 1)
                    }}
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}>
                    <div className="comments">
                        {comments}
                    </div>
                </ScrollViewer>
                <div className="comments-actions">
                    {actions}
                </div>
            </div>
        );
    }

    /**
     * Rendert den Content des Tabs "Aktivitäten".
     */
    private renderActivity(): JSX.Element
    {
        const { transitions, activities, activitySelectedIndex, activitySelectedData } = this.state;

        const activityChanges: JSX.Element[] = [];
        if (activitySelectedData != null)
        {
            switch (activitySelectedData.type)
            {
                case enums.ActivityType.Edit:
                    {
                        for (let change of activitySelectedData.changed_values)
                        {
                            activityChanges.push(
                                <tr>
                                    <td>
                                        <span className="activity-changes_field-name">{change.value_info_text}</span>
                                    </td>
                                    <td>
                                        <ins>{change.new_value}</ins>
                                    </td>
                                    <td>
                                        <del>{change.old_value}</del>
                                    </td>
                                </tr>
                            );
                        }
                    }
                    break;
                case enums.ActivityType.Label:
                    {
                        activityChanges.push(
                            <tr>
                                <td>
                                    <span className="activity-changes_field-name">Labels</span>
                                </td>
                                <td>
                                    {(activitySelectedData.added_labels != null) ? activitySelectedData.added_labels.map((label) => <ins>{label.label}</ins>) : ""}
                                </td>
                                <td>
                                    {(activitySelectedData.removed_labels != null) ? activitySelectedData.removed_labels.map((label) => <ins>{label.label}</ins>) : ""}
                                </td>
                            </tr>
                        );
                    }
                    break;
                case enums.ActivityType.Status:
                    {
                        activityChanges.push(
                            <tr>
                                <td>
                                    <span className="activity-changes_field-name">Status</span>
                                </td>
                                <td>
                                    <ins>{(activitySelectedData.new_status != null) ? activitySelectedData.new_status.status : ""}</ins>
                                </td>
                                <td>
                                    <del>{(activitySelectedData.old_status != null) ? activitySelectedData.old_status.status : ""}</del>
                                </td>
                            </tr>
                        );
                    }
                    break;
                case enums.ActivityType.Following:
                    {
                        activityChanges.push(
                            <tr>
                                <td>
                                    <span className="activity-changes_field-name">Folgetermin</span>
                                </td>
                                <td>
                                    <ins>{"Termin # " + activitySelectedData.following_id.toString() + ": " + activitySelectedData.following_subject}</ins>
                                </td>
                            </tr>
                        );
                    }
                    break;
                case enums.ActivityType.Deleted:
                    {
                        activityChanges.push(
                            <tr>
                                <td>
                                    <span className="activity-changes_field-name">Termin liegt im Archiv</span>
                                </td>
                            </tr>
                        );
                    }
                    break;
            }

        }

        return (
            <div className="event-dialog_content_activity">
                <Expander
                    style={{
                        ...utils.getGridChildStyle(1, 1, 1, 1),
                        marginBottom: 50
                    }}
                    headerText="Status Graph"
                    isExpanded={true}>

                    <div className="transitions">
                        {
                            transitions.map((transition) =>
                            {
                                return (
                                    <div className="transition">
                                        <div className="transition_arrow">
                                            <div className="transition_arrow_tail"></div>
                                            <div className="transition_arrow_head"></div>
                                            <div className="transition_arrow_change-info">
                                                <img
                                                    className="transition_arrow_change-info_img"
                                                    src={transition.profile_picture} />
                                                <span className="transition_arrow_change-info_user">{transition.fullname + " " + transition.change_date.format("L")}</span>
                                            </div>
                                        </div>
                                        <div className="transition_state">
                                            <div
                                                style={{
                                                    backgroundColor: transition.status_color
                                                }}
                                                className="transition_state_color"></div>
                                            <span className="transition_state_text">{transition.status_text}</span>
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>
                </Expander>
                <Expander
                    style={{
                        ...utils.getGridChildStyle(1, 1, 2, 2)
                    }}
                    contentStyle={{
                        height: "95%"
                    }}
                    headerText="Historie"
                    isExpanded={true}>
                    <div className="activity">
                        <ScrollViewer
                            style={{
                                ...utils.getGridChildStyle(1, 1, 1, 1)
                            }}
                            HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                            VerticalScrollBarVisibility={ScrollBarVisibility.auto}>
                            <ListView
                                dataSource={activities}>
                                {
                                    (data: modelsGet.IActivity, index: number) =>
                                    {
                                        let text: string = "";
                                        switch (data.type)
                                        {
                                            case enums.ActivityType.Edit:
                                                text = data.editor_fullname + " änderte den Termin.";
                                                break;
                                            case enums.ActivityType.Label:
                                                text = utilsCalendar.getLabelActivityText(data);
                                                break;
                                            case enums.ActivityType.Status:
                                                text = data.editor_fullname + " änderte den Status.";
                                                break;
                                            case enums.ActivityType.Following:
                                                text = data.editor_fullname + " hat einen Folgetermin erstellt.";
                                                break;
                                            case enums.ActivityType.Deleted:
                                                text = data.editor_fullname + " hat den Termin gelöscht.";
                                                break;
                                        }

                                        return (
                                            <ListViewActivityItem
                                                key={data.id}
                                                index={index}
                                                dataSource={data}
                                                profilePicture={data.editor_profile_picture}
                                                text={text}
                                                isSelected={activitySelectedIndex === index}
                                                date={moment(data.editor_date)}
                                                onClick={(data) => this.setState({
                                                    activitySelectedIndex: index,
                                                    activitySelectedData: data
                                                })} />
                                        );
                                    }
                                }
                            </ListView>
                        </ScrollViewer>
                        {activitySelectedIndex >= 0 && (
                            <div className="activity-changes">
                                <div className="activity-changes_header">
                                    <img
                                        className="activity-changes_header_img"
                                        src={activitySelectedData.editor_profile_picture} />
                                    <div className="activity-changes_header_user">{activitySelectedData.editor_fullname}</div>
                                    <div className="activity-changes_header_date">{moment(activitySelectedData.editor_date).format("LLLL")}</div>
                                    {activitySelectedData.type === enums.ActivityType.Edit && <div className="activity-changes_header_text">Änderte den Termin.</div>}
                                    {activitySelectedData.type === enums.ActivityType.Label && <div className="activity-changes_header_text">{utilsCalendar.getLabelActivityText(activitySelectedData, true)}</div>}
                                    {activitySelectedData.type === enums.ActivityType.Status && <div className="activity-changes_header_text">Änderte den Status.</div>}
                                    {activitySelectedData.type === enums.ActivityType.Following && <div className="activity-changes_header_text">Hat einen Folgetermin erstellt.</div>}
                                    {activitySelectedData.type === enums.ActivityType.Deleted && <div className="activity-changes_header_text">Hat den Termin gelöscht.</div>}
                                </div>
                                <div className="activity-changes_details-container">
                                    <ScrollViewer
                                        HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                                        VerticalScrollBarVisibility={ScrollBarVisibility.auto}>
                                        <div className="activity-changes_details">
                                            {activityChanges}
                                        </div>
                                    </ScrollViewer>
                                </div>
                            </div>
                        )}
                    </div>
                </Expander>
            </div>
        );
    }

    /**
     * Rendert den Content des Tabs "Links".
     */
    private renderLinks(): JSX.Element
    {
        const { links } = this.state;

        const columns: ITableColumn[] = [
            {
                header: "Link",
                width: 200,
                cell: (data: modelsGet.ILink) =>
                {
                    return (
                        <div
                            style={{
                                width: 200
                            }}
                            className="link-column">
                            <i
                                style={{
                                    color: data.status_color
                                }}
                                className={`link-column_icon mdi mdi-${data.status_icon}`}>
                            </i>
                            <div className="link-column_id">
                                {data.event_id}
                            </div>
                            <div className="link-column_text">
                                {data.subject}
                            </div>
                        </div>
                    );
                }
            },
            {
                header: "Typ",
                accessor: "type",
                width: 200
            },
            {
                header: "Kalender",
                accessor: "calendar",
                width: 200
            },
            {
                header: "Datum",
                width: 200,
                computedValue: (data: modelsGet.ILink) =>
                {
                    return moment(data.starttime).format("L");
                }
            },
            {
                header: "Zuletzt aktualisiert",
                width: 200,
                computedValue: (data: modelsGet.ILink) =>
                {
                    return "Aktualisiert am " + moment(data.last_changed).format("L");
                }
            }
        ];

        return (
            <div className="event-dialog_content_links">
                <Expander
                    style={{
                        height: "99%"
                    }}
                    contentStyle={{
                        height: "100%",
                        position: "relative"
                    }}
                    headerText="Erst- oder Folgetermine"
                    isExpanded={true}>
                    {links && links.length > 0 ?
                        <Table
                            dataSource={links}
                            columns={columns}
                            keyAccessor="event_id"
                            onRowDoubleClick={this.onLinkRowDoubleClick} />
                        :
                        <div className="links-empty">Es sind keine Erst- und Folgetermine vorhanden.</div>
                    }
                </Expander>
            </div>
        );
    }

    /**
     * Rendert den Dialog zum löschen eines Termins.
     */
    private renderDeleteDialog(): JSX.Element
    {
        const { appointment } = this.state;

        return (
            <DeleteDialog
                appointmentId={appointment.id}
                isArchived={appointment.is_archived}
                onClose={this.onDeleteDialogClosing} />
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const { isModal } = this.props;
        const {
            isLoading,
            mode,
            selectedCalendar,
            appointment,
            isDeleteDialogOpen,
            settings
        } = this.state;

        // Title ermitteln.
        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var titleIndent: number = null;
        var color: string = null;

        if (settings.eventFields == null)
            return null;

        const isDeceasedExpanderVisible: boolean = settings.eventFields.any(f => (
            f === "Nachname (Verstorbener)" ||
            f === "Vorname (Verstorbener)" ||
            f === "Geburtsname (Verstorbener)" ||
            f === "Geburtsdatum (Verstorbener)" ||
            f === "Sterbedatum (Verstorbener)" ||
            f === "Sterbeort (Verstorbener)" ||
            f === "Konfession (Verstorbener)"
        ));
        const isCustomerExpanderVisible: boolean = settings.eventFields.any(f => (
            f === "Anrede (Ansprechpartner)" ||
            f === "Nachname (Ansprechpartner)" ||
            f === "Vorname (Ansprechpartner)" ||
            f === "Straße (Ansprechpartner)" ||
            f === "Hausnummer (Ansprechpartner)" ||
            f === "Land (Ansprechpartner)" ||
            f === "PLZ (Ansprechpartner)" ||
            f === "Ort (Ansprechpartner)" ||
            f === "Rufnummer (Ansprechpartner)"
        ));

        if (!isLoading)
        {
            if (mode === Mode.create)
            {
                icon = "plus";
                title = "Neuen Termin in " + selectedCalendar.name + " erstellen";
            }
            else if (mode === Mode.copy)
            {
                icon = "content-paste";
                title = "Termin #" + appointment.id + " kopieren";
            }
            else if (mode === Mode.followUp)
            {
                icon = "calendar-plus";
                title = "Neuen Folgetermin in " + selectedCalendar.name + " erstellen";
            }
            else if (mode === Mode.edit)
            {
                icon = appointment.status_icon;
                title = appointment.calendar_name + ": Termin " + appointment.id;
                titleIndent = 20;
                color = utilsCalendar.getEventColor(appointment);
            }
        }

        return (
            <div>
                <Window
                    isOpen={true}
                    titleIcon={icon}
                    titleIconColor={color}
                    title={title}
                    titleIndent={titleIndent}
                    canContentExtendToTitle={true}
                    onClosing={this.onClosing}
                    isMaximized={!isModal}>
                    {this.renderContent(isDeceasedExpanderVisible, isCustomerExpanderVisible)}
                </Window>

                {isDeleteDialogOpen && this.renderDeleteDialog()}
            </div>
        );
    }
}